import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { formatDecimal } from '../../lib/utils';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Lock, RefreshCw, GripVertical, Upload, X, ZoomIn, FolderOpen, ExternalLink, FileText, Table2, ChevronDown, ChevronRight, Download, Printer, ListChecks, Paperclip, Eye } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

declare global {
  interface Window {
    Dropbox: {
      choose(options: {
        success: (files: Array<{ name: string; link: string }>) => void;
        linkType: 'preview' | 'direct';
        multiselect: boolean;
        folderselect: boolean;
      }): void;
    };
  }
}
import NodeCostTree, { badgeFor } from './NodeCostTree';
import { groupByChapter, SENSE_CHAPTER } from './nodeUtils';
import {
  fmt2, fmt3, type DetallDisplayRow, detallHeaders, detallRowToCells, detallNumFormats,
  sanitizeFilename, detallColWidths, exportDetallToExcel, DETALL_FOTO_COL_INDEX,
} from './detallExcelExport';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../core/apiClient';
import apiClient from '../../core/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

type Nature    = 'MATERIAL' | 'MO_PROPIA' | 'MO_SUBCONTRACT' | 'SERVEIS' | 'ALTRES';
type NodeType  = 'COMPOSITE' | 'ESCANDALL';
type ChildType = 'MATERIAL' | 'NODE';

const NATURE_LABELS: Record<Nature, string> = {
  MATERIAL:       'Material',
  MO_PROPIA:      'M.O. pròpia',
  MO_SUBCONTRACT: 'M.O. subcontractada',
  SERVEIS:        'Serveis',
  ALTRES:         'Altres',
};

interface SelectOption { id: number; code: string; name: string; marginPercentage?: number | null; }
interface UnitType     { code: string; name: string; description: string; }
interface NodeTypeOption { code: string; name: string; isActive?: number; }
interface SimpleMaterial { id: string; code: string; name: string; unit: string; costPerUnit: number; weightKgPerUnit: number; elementTypeId?: number | null; compositeCodeId?: number | null; pricePerMagnitude?: number; technicalImageUrl?: string | null; }
interface ElementTypeOption { id: number; code: string; name: string; }
interface NodeDocumentRow { id: string; name: string; fileName: string; fileSize: number; mimeType?: string; createdAt: string; }

// Infereix la naturalesa (Tipus) de la línia a partir del Tipus d'elements del material,
// per codi/nom (no per id — l'id és un AUTO_INCREMENT que no és fiable com a referència fixa).
const NATURE_KEYWORDS: { pattern: RegExp; nature: Nature }[] = [
  { pattern: /subcontract/i,                   nature: 'MO_SUBCONTRACT' },
  { pattern: /treball|obra|personal/i,          nature: 'MO_PROPIA' },
  { pattern: /servei|transport|log[ií]stic/i,   nature: 'SERVEIS' },
];
function natureForElementType(et: ElementTypeOption | undefined): Nature | undefined {
  if (!et) return undefined;
  const text = `${et.code} ${et.name}`;
  return NATURE_KEYWORDS.find(({ pattern }) => pattern.test(text))?.nature;
}

// Normalitza la descripció d'una línia de mà d'obra a un dels literals estàndard, perquè
// petites variants de redacció ("Operari de taller per fabricació", "Operari a taller per
// fabricació"...) s'agrupin totes sota el mateix nom a Ratis/Dades Econòmiques.
const LABOR_CATEGORY_KEYWORDS: { pattern: RegExp; label: string }[] = [
  { pattern: /oficina/i,        label: 'Operari oficina tècnica' },
  { pattern: /extern/i,         label: 'Operari extern' },
  { pattern: /muntatge/i,       label: 'Operari muntatge' },
  { pattern: /fabricaci[oó]/i,  label: 'Operari taller fabricació' },
  { pattern: /producci[oó]/i,   label: 'Operari taller producció' },
];
function laborCategoryLabel(description: string): string {
  const found = LABOR_CATEGORY_KEYWORDS.find(({ pattern }) => pattern.test(description));
  return found?.label ?? (description.trim() || 'Altres hores');
}

interface NodeSummary  { id: string; code?: string; name: string; unit: string; cachedTotalDirectCost: number; compositeCodeId?: number | null; }

interface NodeFormLine {
  id?:               string;
  childType:         ChildType;
  childMaterialId?:  string | null;
  childNodeId?:      string | null;
  // Tipus de Producte del material/escandall vinculat (no és una columna pròpia de la línia,
  // s'usa nomès per calcular el Marge per defecte des de Tipus de Producte > Marge comercial %).
  compositeCodeId?:  number | null;
  // Imatge tècnica del material/escandall fill vinculat (nomes lectura, resolta pel backend).
  elementImageUrl?:  string | null;
  description:       string;
  nature:            Nature;
  unit:              string;
  quantity:          number;
  dimensionA:        number;
  dimensionB:        number;
  dimensionH:        number;
  measurement:       number;        // provisional client-side calc
  isPriceLocked:     number;        // 0 | 1
  unitCostOverride:  number | null;
  resolvedUnitCost:  number;        // from backend
  lineTotal:         number;        // from backend
  hoursProduction:   number;
  hoursFabrication:  number;
  hoursAssembly:     number;
  weightKg:          number;
  referencia?:       string;
  chapter?:          string;
  position?:         string;
  detail?:           string;
  marge?:            number | null;
  descompte?:        number | null;
  observations?:     string;
}

// Instruccions de muntatge/configuracio de l'escandall — llista simple i
// reordenable per arrossegament (Nº posicio / Capítol / Comentari).
interface NodeInstructionRow {
  id?:       string;
  chapter:   string;
  comment:   string;
  photoUrl?: string | null;
}

interface NodeFormData {
  code:                        string;
  name:                        string;
  description:                 string;
  observations:                string;
  imageUrl:                    string | null;
  nodeType:                    NodeType;
  unit:                        string;
  amidament:                   number;
  compositeCodeId?:            number;
  compositeSubfamilyId?:       number;
  compositeProductId?:         number;
  compositeModelId?:           number;
  compositeVariantId?:         number;
  location:                    string;
  folderPath:                  string;
  planReference:               string;
  nodeDate:                    string;
  isSemiFinished:              number;
  indirectCostPercentage:      number;
  coefVenda?:                  number;
  preuVenda?:                  number;
  cachedTotalMaterialsCost:        number;
  cachedTotalLaborOwnCost:         number;
  cachedTotalLaborSubcontractCost: number;
  cachedTotalServicesCost:         number;
  cachedTotalOtherCost:            number;
  cachedTotalDirectCost:           number;
  cachedTotalIndirectCost:         number;
  cachedTotalCost:                 number;
  cachedTotalHoursOwn:             number;
  cachedTotalHoursSubcontract:     number;
  cachedTotalHours:                number;
  cachedTotalWeightKg:             number;
  cachedCostPerKg:                 number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const emptyFormData = (): NodeFormData => ({
  code: '', name: '', description: '', observations: '', imageUrl: null,
  nodeType: 'COMPOSITE', unit: 'Ut', amidament: 1, compositeCodeId: undefined,
  location: '', folderPath: '', planReference: '', nodeDate: '',
  isSemiFinished: 0, indirectCostPercentage: 5,
  coefVenda: undefined, preuVenda: undefined,
  cachedTotalMaterialsCost: 0, cachedTotalLaborOwnCost: 0,
  cachedTotalLaborSubcontractCost: 0, cachedTotalServicesCost: 0,
  cachedTotalOtherCost: 0, cachedTotalDirectCost: 0,
  cachedTotalIndirectCost: 0, cachedTotalCost: 0,
  cachedTotalHoursOwn: 0, cachedTotalHoursSubcontract: 0,
  cachedTotalHours: 0, cachedTotalWeightKg: 0, cachedCostPerKg: 0,
});

const emptyMaterialLine = (): NodeFormLine => ({
  childType: 'MATERIAL', childMaterialId: null, childNodeId: null, compositeCodeId: null,
  description: '', nature: 'MATERIAL', unit: 'Ut',
  quantity: 0, dimensionA: 0, dimensionB: 0, dimensionH: 0,
  measurement: 0, isPriceLocked: 0, unitCostOverride: null,
  resolvedUnitCost: 0, lineTotal: 0,
  hoursProduction: 0, hoursFabrication: 0, hoursAssembly: 0,
  weightKg: 0, referencia: '', chapter: '', position: '', detail: '',
  marge: null, descompte: null,
});

const emptyNodeLine = (): NodeFormLine => ({
  ...emptyMaterialLine(), childType: 'NODE',
});

function arrayMove<T>(arr: T[], from: number, to: number): T[] {
  const result = [...arr];
  const [item] = result.splice(from, 1);
  result.splice(to, 0, item);
  return result;
}

function calcMeasurement(qty: number, a: number, b: number, h: number): number {
  if (a > 0 && b > 0 && h > 0) return qty * a * b * h;
  if (a > 0 && b > 0)          return qty * a * b;
  if (a > 0)                   return qty * a;
  return qty;
}

function applyLineTotals(line: NodeFormLine): NodeFormLine {
  const measurement = calcMeasurement(
    Number(line.quantity) || 0,
    Number(line.dimensionA) || 0,
    Number(line.dimensionB) || 0,
    Number(line.dimensionH) || 0,
  );
  const lineTotal = line.isPriceLocked
    ? measurement * (Number(line.unitCostOverride) || 0)
    : measurement * Number(line.resolvedUnitCost || 0);
  return { ...line, measurement, lineTotal };
}

// ─── Valors Calculats — files reutilitzables (evita taules amb columnes que no quadren) ──

// value = només la xifra (sense unitat), perquè les xifres alineïn el decimal encara que
// la unitat (h, Kg, €/h...) tingui longituds diferents entre files.
function ValorsRow({ label, value, unit, note, bold, accent }: {
  label: string; value: string; unit?: string; note?: string; bold?: boolean; accent?: boolean;
}) {
  return (
    <div className={`flex items-baseline gap-3 py-0.5 text-xs ${bold ? 'font-semibold' : ''}`}>
      <span className={`flex-1 min-w-0 truncate ${accent ? 'text-primary' : ''}`} title={label}>
        {label}
      </span>
      <span className="flex items-baseline gap-1 shrink-0">
        <span className={`w-16 tabular-nums text-right whitespace-nowrap ${accent ? 'text-primary' : ''}`}>{value}</span>
        <span className={`w-9 text-left text-muted-foreground whitespace-nowrap ${accent ? 'text-primary' : ''}`}>{unit ?? ''}</span>
      </span>
      <span className="shrink-0 w-20 text-left text-xs text-muted-foreground whitespace-nowrap">{note ?? ''}</span>
    </div>
  );
}

// value = Import (preu per 1 unitat de l'escandall, tal com surt del Descompost).
// pct = value ÷ Base Imposable. ua = Valors/UA = value ÷ Amidament.
function DEconRow({ label, value, pct, ua, uaUnit, bold, indent, accent }: {
  label: React.ReactNode; value: number; pct: number; ua: number | null; uaUnit: string; bold?: boolean; indent?: boolean; accent?: boolean;
}) {
  return (
    <div className={`flex items-baseline gap-3 py-0.5 text-xs ${bold ? 'font-semibold' : ''} ${accent ? 'text-primary' : ''}`}>
      <span className={`flex-1 min-w-0 flex items-center gap-1.5 truncate ${indent ? 'pl-3' : ''} ${accent ? 'text-primary' : bold ? '' : 'text-muted-foreground'}`}>{label}</span>
      <span className="w-20 shrink-0 tabular-nums text-right whitespace-nowrap">{fmt2(value)} €</span>
      <span className="w-24 shrink-0 tabular-nums text-right text-xs text-muted-foreground whitespace-nowrap">{ua != null ? `${fmt2(ua)} €` : '—'}</span>
      <span className="w-12 shrink-0 tabular-nums text-right text-xs text-muted-foreground whitespace-nowrap">{fmt2(pct)}</span>
    </div>
  );
}

// ─── Detall d'elements / Detall descompost — vistes read-only agrupades ──────
// Reutilitzen les mateixes columnes que la taula editable del Detall.

const DETALL_COLS = [16, 22, 85, 48, 230, 230, 230, 52, 52, 52, 52, 52, 62, 24, 70, 70, 58, 42, 45, 55, 58, 48];
const DETALL_COLS_PCT = [...DETALL_COLS.slice(0, 21), 48, ...DETALL_COLS.slice(21)];

// ── Exportar (Excel/CSV) i Imprimir (PDF via diàleg d'impressió del navegador) ──

function htmlEscape(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Cel·la per a l'export a Excel — si fmt no és null, força Excel a mostrar
// sempre aquest nombre de decimals (mso-number-format), en lloc de deixar
// que el "General" per defecte retalli els zeros finals.
function excelTd(value: string, fmt: number | null): string {
  if (fmt != null && value !== '') {
    return `<td style="mso-number-format:'0${fmt > 0 ? `\\.${'0'.repeat(fmt)}` : ''}'">${htmlEscape(value)}</td>`;
  }
  return `<td>${htmlEscape(value)}</td>`;
}

function printDetallTable(
  title: string,
  groups: { key: string; label: string; rows: DetallDisplayRow[] }[],
  withPctImport?: boolean,
) {
  const headers = detallHeaders(withPctImport);
  const win = window.open('', '_blank');
  if (!win) return;
  const bodyHtml = groups.map((g) => {
    const groupRow = `<tr><td colspan="${headers.length}" class="grp">${g.label}</td></tr>`;
    const itemRows = g.rows.map((r) =>
      `<tr>${detallRowToCells(r, withPctImport).map((c, i) => (
        i === DETALL_FOTO_COL_INDEX
          ? `<td>${r.elementImageUrl ? `<img src="${r.elementImageUrl}" width="28" height="28" style="object-fit:cover;" />` : ''}</td>`
          : `<td>${c}</td>`
      )).join('')}</tr>`,
    ).join('');
    return groupRow + itemRows;
  }).join('');
  win.document.write(`<!doctype html><html><head><title>${title}</title>
    <meta charset="utf-8">
    <style>
      body { font-family: Arial, sans-serif; font-size: 10px; margin: 16px; }
      h1 { font-size: 14px; margin: 0 0 10px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ccc; padding: 2px 5px; text-align: right; white-space: nowrap; }
      th:nth-child(-n+6), td:nth-child(-n+6) { text-align: left; white-space: normal; }
      thead { background: #e2e8f0; }
      .grp { text-align: left; font-weight: 600; background: #f1f5f9; }
    </style>
    </head><body>
    <h1>${title}</h1>
    <table>
      <thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
      <tbody>${bodyHtml}</tbody>
    </table>
    </body></html>`);
  win.document.close();
  win.focus();
  win.print();
}

const INSTRUCCIONS_HEADERS = ['Nº', 'Referència', 'Descripció'];

function instruccionsRowsToCells(rows: NodeInstructionRow[]): string[][] {
  return rows.map((row, i) => [String(i + 1), row.chapter || '', row.comment || '']);
}

function exportInstruccionsToExcel(filename: string, rows: NodeInstructionRow[], title?: string) {
  const titleHtml = title ? `<tr><td colspan="${INSTRUCCIONS_HEADERS.length}" style="font-weight:bold;font-size:14px;">${htmlEscape(title)}</td></tr><tr><td colspan="${INSTRUCCIONS_HEADERS.length}"></td></tr>` : '';
  const headHtml = `<tr>${INSTRUCCIONS_HEADERS.map((h) => `<th>${htmlEscape(h)}</th>`).join('')}</tr>`;
  const bodyHtml = instruccionsRowsToCells(rows).map((cells) =>
    `<tr>${cells.map((c) => `<td>${htmlEscape(c)}</td>`).join('')}</tr>`,
  ).join('');
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head><meta charset="utf-8">
    <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Instruccions</x:Name>
    <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>
    <body><table border="1">${titleHtml}${headHtml}${bodyHtml}</table></body></html>`;
  const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function printInstruccions(title: string, rows: NodeInstructionRow[]) {
  const win = window.open('', '_blank');
  if (!win) return;
  const bodyHtml = instruccionsRowsToCells(rows).map((cells) =>
    `<tr>${cells.map((c) => `<td>${c}</td>`).join('')}</tr>`,
  ).join('');
  win.document.write(`<!doctype html><html><head><title>${title}</title>
    <meta charset="utf-8">
    <style>
      body { font-family: Arial, sans-serif; font-size: 10px; margin: 16px; }
      h1 { font-size: 14px; margin: 0 0 10px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; }
      th:first-child, td:first-child { width: 40px; text-align: right; }
      thead { background: #e2e8f0; }
    </style>
    </head><body>
    <h1>${title}</h1>
    <table>
      <thead><tr>${INSTRUCCIONS_HEADERS.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
      <tbody>${bodyHtml}</tbody>
    </table>
    </body></html>`);
  win.document.close();
  win.focus();
  win.print();
}

function DetallColHead({ withPctImport }: { withPctImport?: boolean }) {
  const cols = withPctImport ? DETALL_COLS_PCT : DETALL_COLS;
  return (
    <>
      <colgroup>
        {cols.map((w, i) => (
          <col key={i} style={{ width: `${w}px` }} />
        ))}
        <col style={{ width: '24px' }} />
      </colgroup>
      <thead className="bg-slate-200">
        <tr>
          <th />
          <th className="px-1 py-0.5 text-left">#</th>
          <th className="px-1 py-0.5 text-left">Cap.</th>
          <th className="px-1 py-0.5 text-left">Ref. Plànol</th>
          <th className="px-1 py-0.5 text-left">Descripció</th>
          <th className="px-1 py-0.5 text-left">Operació</th>
          <th className="px-1 py-0.5 text-left">Element</th>
          <th className="px-1 py-0.5 text-center" title="Tipus d'element">Tipus</th>
          <th className="px-1 py-0.5 text-center">UA</th>
          <th className="px-1 py-0.5 text-right">Q.</th>
          <th className="px-1 py-0.5 text-right">Mida A</th>
          <th className="px-1 py-0.5 text-right">Mida B</th>
          <th className="px-1 py-0.5 text-right">Mida C</th>
          <th className="px-1 py-0.5 text-right">Amidament</th>
          <th className="px-1 py-0.5 text-center" title="Preu bloquejat">🔒</th>
          <th className="px-1 py-0.5 text-right">Cost/U</th>
          <th className="px-1 py-0.5 text-right">Cost total</th>
          <th className="px-1 py-0.5 text-right">Marge</th>
          <th className="px-1 py-0.5 text-right">Descompte</th>
          <th className="px-1 py-0.5 text-right">Preu</th>
          <th className="px-1 py-0.5 text-right">Import</th>
          {withPctImport && <th className="px-1 py-0.5 text-right" title="% sobre el total d'Import">% Import</th>}
          <th className="px-1 py-0.5 text-right">Pes</th>
          <th />
        </tr>
      </thead>
    </>
  );
}

function DetallReadRow({ r, withPctImport }: { r: DetallDisplayRow; withPctImport?: boolean }) {
  return (
    <tr>
      <td />
      <td className="px-1 py-0.5">{r.num}</td>
      <td className="px-1 py-0.5 truncate">{r.cap}</td>
      <td className="px-1 py-0.5 truncate">{r.planol}</td>
      <td className="px-1 py-0.5 truncate">{r.comentari}</td>
      <td className="px-1 py-0.5 truncate">{r.observacions}</td>
      <td className="px-1 py-0.5 truncate">{r.element}</td>
      <td className="px-1 py-0.5 text-center">
        <span className={`inline-block text-[9px] font-bold px-0.5 rounded leading-3 ${r.badge.cls}`}>{r.badge.text}</span>
      </td>
      <td className="px-1 py-0.5 text-center">{r.ua}</td>
      <td className="px-1 py-0.5 text-right">{r.un}</td>
      <td className="px-1 py-0.5 text-right">{r.a}</td>
      <td className="px-1 py-0.5 text-right">{r.b}</td>
      <td className="px-1 py-0.5 text-right">{r.c}</td>
      <td className="px-1 py-0.5 text-right">{r.amidament}</td>
      <td className="px-1 py-0.5 text-center">{r.locked ? '🔒' : ''}</td>
      <td className="px-1 py-0.5 text-right whitespace-nowrap">{fmt2(r.costU)}</td>
      <td className="px-1 py-0.5 text-right font-medium whitespace-nowrap">{fmt2(r.costTotal)}</td>
      <td className="px-1 py-0.5 text-right whitespace-nowrap">{r.marge != null ? fmt2(r.marge) : ''}</td>
      <td className="px-1 py-0.5 text-right">{r.descompte}</td>
      <td className="px-1 py-0.5 text-right whitespace-nowrap">{fmt2(r.preu)}</td>
      <td className="px-1 py-0.5 text-right font-medium whitespace-nowrap">{fmt2(r.import)}</td>
      {withPctImport && (
        <td className="px-1 py-0.5 text-right whitespace-nowrap">{r.pctImport != null ? `${fmt2(r.pctImport)} %` : ''}</td>
      )}
      <td className="px-1 py-0.5 text-right whitespace-nowrap">{fmt2(r.pes)}</td>
      <td />
    </tr>
  );
}

function DetallSubtotalRow({ label, rows, withPctImport }: { label: string; rows: DetallDisplayRow[]; withPctImport?: boolean }) {
  const informedMarge = rows.map((r) => r.marge).filter((m): m is number => m != null && m > 0);
  return (
    <tr className="bg-slate-50 font-medium border-b border-slate-300" style={{ fontSize: '9px' }}>
      <td colSpan={16} className="px-1 py-0.5 text-right text-muted-foreground">{label}:</td>
      <td className="px-1 py-0.5 text-right whitespace-nowrap">{fmt2(rows.reduce((s, r) => s + r.costTotal, 0))}</td>
      <td className="px-1 py-0.5 text-right whitespace-nowrap">
        {informedMarge.length > 0 ? fmt2(informedMarge.reduce((s, m) => s + m, 0) / informedMarge.length) : ''}
      </td>
      <td />
      <td />
      <td className="px-1 py-0.5 text-right whitespace-nowrap">{fmt2(rows.reduce((s, r) => s + r.import, 0))}</td>
      {withPctImport && (
        <td className="px-1 py-0.5 text-right whitespace-nowrap">
          {fmt2(rows.reduce((s, r) => s + (r.pctImport ?? 0), 0))} %
        </td>
      )}
      <td className="px-1 py-0.5 text-right whitespace-nowrap">{fmt2(rows.reduce((s, r) => s + r.pes, 0))} Kg</td>
      <td />
    </tr>
  );
}

function DetallReadonlyTable({ groups, withPctImport }: { groups: { key: string; label: string; rows: DetallDisplayRow[] }[]; withPctImport?: boolean }) {
  const allRows = groups.flatMap((g) => g.rows);
  if (allRows.length === 0) {
    return <div className="px-4 py-4 text-center text-muted-foreground text-xs">Sense línies</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="escandall-lines-table" style={{ width: '100%', tableLayout: 'auto', fontSize: '9px', lineHeight: '1.2' }}>
        <DetallColHead withPctImport={withPctImport} />
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.key}>
              <tr className="bg-slate-100 border-t border-slate-300">
                <td colSpan={withPctImport ? 24 : 23} className="px-2 py-0.5 font-semibold text-slate-600" style={{ fontSize: '9px' }}>{g.label}</td>
              </tr>
              {g.rows.map((r) => <DetallReadRow key={r.key} r={r} withPctImport={withPctImport} />)}
              <DetallSubtotalRow label={`Subtotal ${g.label}`} rows={g.rows} withPctImport={withPctImport} />
            </Fragment>
          ))}
        </tbody>
        <tfoot className="bg-slate-100 font-medium" style={{ fontSize: '9px' }}>
          <DetallSubtotalRow label="Totals" rows={allRows} withPctImport={withPctImport} />
        </tfoot>
      </table>
    </div>
  );
}

function DetallCollapsiblePanel({ title, nodeName, groups, withPctImport }: { title: string; nodeName: string; groups: { key: string; label: string; rows: DetallDisplayRow[] }[]; withPctImport?: boolean }) {
  const [open, setOpen] = useState(false);
  const fullTitle = nodeName ? `${nodeName} — ${title}` : title;
  return (
    <div className="border rounded">
      <div className="w-full flex items-center justify-between bg-slate-100 px-4 py-2 font-medium text-sm rounded-t gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 hover:opacity-70 transition-opacity"
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span>{title}</span>
        </button>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="text-[11px] h-7" onClick={() => exportDetallToExcel(`${sanitizeFilename(fullTitle)}.xls`, groups, withPctImport, fullTitle)}>
            <Download className="h-3.5 w-3.5 mr-1" /> Excel
          </Button>
          <Button size="sm" variant="outline" className="text-[11px] h-7" onClick={() => printDetallTable(fullTitle, groups, withPctImport)}>
            <Printer className="h-3.5 w-3.5 mr-1" /> PDF
          </Button>
        </div>
      </div>
      {open && <DetallReadonlyTable groups={groups} withPctImport={withPctImport} />}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function NodeForm() {
  const navigate  = useNavigate();
  const { id }    = useParams<{ id: string }>();
  const isNew     = !id;

  const [formData, setFormData]   = useState<NodeFormData>(emptyFormData());
  const [lines, setLines]         = useState<NodeFormLine[]>([]);
  const [instructions, setInstructions] = useState<NodeInstructionRow[]>([]);
  const [detallOpen, setDetallOpen] = useState(true);
  const [compositeCodes, setCompositeCodes] = useState<SelectOption[]>([]);
  const [compositeSubfamilies, setCompositeSubfamilies] = useState<{ id: number; code: string; name: string; compositeCodeId: number; isActive: number | boolean }[]>([]);
  const [compositeProducts, setCompositeProducts] = useState<{ id: number; code: string; name: string; subfamilyId: number; isActive: number | boolean }[]>([]);
  const [compositeModels, setCompositeModels] = useState<{ id: number; code: string; name?: string | null; productId: number; isActive: number | boolean }[]>([]);
  const [compositeVariants, setCompositeVariants] = useState<{ id: number; code: string; name?: string | null; modelId: number; isActive: number | boolean }[]>([]);

  // Marge per defecte d'una línia: el "Marge comercial %" del Tipus de Producte vinculat
  // al material o escandall de la línia; 0 si no en té (p.ex. línia de text lliure).
  const marginForLine = (l: NodeFormLine): number => {
    if (!l.compositeCodeId) return 0;
    const cc = compositeCodes.find((c) => c.id === l.compositeCodeId);
    return cc?.marginPercentage != null ? Number(cc.marginPercentage) : 0;
  };

  // ── "Detall d'elements" / "Detall descompost" — vistes read-only agrupades ──

  const lineToDisplayRow = (line: NodeFormLine, num: number): DetallDisplayRow => {
    const effMarge = line.marge ?? marginForLine(line);
    const preu = Number(line.resolvedUnitCost || 0) * effMarge * (1 - Number(line.descompte ?? 0) / 100);
    return {
      key: `l-${num}`,
      num: String(num),
      cap: line.chapter || '',
      planol: line.referencia || '',
      comentari: line.detail || '',
      element: line.description || '',
      badge: badgeFor(line.childType, line.nature),
      ua: line.unit,
      un: line.quantity ? fmt2(line.quantity) : '',
      a: line.dimensionA ? fmt3(line.dimensionA) : '',
      b: line.dimensionB ? fmt3(line.dimensionB) : '',
      c: line.dimensionH ? fmt3(line.dimensionH) : '',
      amidament: fmt2(line.measurement),
      locked: !!line.isPriceLocked,
      costU: Number(line.resolvedUnitCost || 0),
      costTotal: Number(line.lineTotal || 0),
      marge: effMarge,
      descompte: line.descompte != null ? fmt2(line.descompte) : '',
      preu,
      import: Number(line.measurement || 0) * preu,
      observacions: line.observations || '',
      pes: Number(line.weightKg || 0) * Number(line.measurement || 0),
      elementImageUrl: line.elementImageUrl ?? null,
    };
  };

  // Tipus: agrupa Materials (incl. escandalls fills), Mà d'obra (pròpia+subcontractada), Serveis, Altres.
  const tipusCategoryOf = (line: NodeFormLine): { key: string; label: string } => {
    if (line.childType === 'NODE') return { key: 'MATERIAL', label: 'Materials' };
    switch (line.nature) {
      case 'MO_PROPIA':
      case 'MO_SUBCONTRACT': return { key: 'MO', label: "Mà d'obra" };
      case 'SERVEIS':        return { key: 'SERVEIS', label: 'Serveis' };
      case 'MATERIAL':       return { key: 'MATERIAL', label: 'Materials' };
      default:                return { key: 'ALTRES', label: 'Altres' };
    }
  };
  // Detall (per capítol, mateix ordre que la taula editable) — usat per Excel/PDF
  const detallByChapterGroups = groupByChapter(lines).sort((a, b) => {
    if (a.chapter === SENSE_CHAPTER) return 1;
    if (b.chapter === SENSE_CHAPTER) return -1;
    return 0;
  }).map((group) => ({
    key: group.chapter,
    label: group.chapter,
    rows: group.items.map(({ item, originalIdx }) => lineToDisplayRow(item, originalIdx + 1)),
  }));

  const detallElementsGroups = (() => {
    const order = [
      { key: 'MATERIAL', label: 'Materials' },
      { key: 'MO', label: "Mà d'obra" },
      { key: 'SERVEIS', label: 'Serveis' },
      { key: 'ALTRES', label: 'Altres' },
    ];
    return order
      .map(({ key, label }) => ({
        key, label,
        rows: lines
          .map((l, i) => ({ l, num: i + 1 }))
          .filter(({ l }) => tipusCategoryOf(l).key === key)
          .map(({ l, num }) => lineToDisplayRow(l, num)),
      }))
      .filter((g) => g.rows.length > 0);
  })();

  // Descompost: agrupa per element (descripció), sumant/ponderant, ordenat de més a menys Cost total.
  const detallDescompostGroups = (() => {
    interface Agg {
      description: string; badge: { text: string; cls: string }; unit: string;
      amidament: number; costTotal: number; import: number; pes: number;
      margeSum: number; margeCount: number; costUxAmid: number;
      imageUrl: string | null;
    }
    const map = new Map<string, Agg>();
    lines.forEach((l) => {
      const key = l.description?.trim() || '(sense nom)';
      const effMarge = l.marge ?? marginForLine(l);
      const preu = Number(l.resolvedUnitCost || 0) * effMarge * (1 - Number(l.descompte ?? 0) / 100);
      const imp = Number(l.measurement || 0) * preu;
      const g = map.get(key) ?? {
        description: key, badge: badgeFor(l.childType, l.nature), unit: l.unit,
        amidament: 0, costTotal: 0, import: 0, pes: 0, margeSum: 0, margeCount: 0, costUxAmid: 0,
        imageUrl: l.elementImageUrl ?? null,
      };
      g.amidament   += Number(l.measurement || 0);
      g.costTotal   += Number(l.lineTotal || 0);
      g.import      += imp;
      g.pes         += Number(l.weightKg || 0) * Number(l.measurement || 0);
      if (effMarge > 0) { g.margeSum += effMarge; g.margeCount += 1; }
      g.costUxAmid  += Number(l.resolvedUnitCost || 0) * Number(l.measurement || 0);
      map.set(key, g);
    });
    const totalImportAll = Array.from(map.values()).reduce((s, g) => s + g.import, 0);
    const rows: DetallDisplayRow[] = Array.from(map.values())
      .sort((a, b) => b.costTotal - a.costTotal)
      .map((g, i) => ({
        key: `e-${i}`,
        num: String(i + 1),
        cap: '',
        planol: '',
        comentari: '',
        element: g.description,
        badge: g.badge,
        ua: g.unit,
        un: '',
        a: '', b: '', c: '',
        amidament: fmt2(g.amidament),
        locked: false,
        costU: g.amidament > 0 ? g.costUxAmid / g.amidament : 0,
        costTotal: g.costTotal,
        marge: g.margeCount > 0 ? g.margeSum / g.margeCount : null,
        descompte: '',
        preu: g.amidament > 0 ? g.import / g.amidament : 0,
        import: g.import,
        observacions: '',
        pctImport: totalImportAll > 0 ? (g.import / totalImportAll) * 100 : 0,
        pes: g.pes,
        elementImageUrl: g.imageUrl,
      }));
    return [{ key: 'descompost', label: 'Elements', rows }];
  })();

  // Preu venda = suma de la columna Import (Amidament × Preu) de totes les línies.
  const totalImport = lines.reduce((s, l) =>
    s + Number(l.measurement || 0) * Number(l.resolvedUnitCost || 0) * Number(l.marge ?? marginForLine(l)) * (1 - Number(l.descompte ?? 0) / 100)
  , 0);

  // Ratis / Dades Econòmiques (pestanya "Dades") — la mà d'obra (MO_PROPIA/MO_SUBCONTRACT)
  // s'agrupa dinàmicament per l'element de la línia (el nom del material fet servir),
  // ja que cada escandall pot incloure tipus de personal diferents (Producció, Fabricació,
  // Muntatge, Personal extern, Oficina tècnica...), no una llista fixa de 3 tipus.
  // Cost Compres/Cost Hores són COSTOS purs (columna "Cost total" del Detall), no Import
  // (que ja porta Marge/Descompte aplicats — són conceptes diferents).
  const directLaborGroups = (() => {
    const map = new Map<string, { hours: number; cost: number }>();
    lines
      .filter((l) => l.nature === 'MO_PROPIA' || l.nature === 'MO_SUBCONTRACT')
      .forEach((l) => {
        const key = laborCategoryLabel(l.description ?? '');
        const g = map.get(key) ?? { hours: 0, cost: 0 };
        g.hours += Number(l.measurement || 0);
        g.cost  += Number(l.lineTotal || 0);
        map.set(key, g);
      });
    return Array.from(map.entries()).map(([label, v]) => ({ label, ...v }));
  })();
  const baseImposable = totalImport;
  const costMaterials = formData.cachedTotalMaterialsCost;
  const costServeis   = formData.cachedTotalServicesCost;
  const costCompres   = costMaterials + costServeis + formData.cachedTotalIndirectCost;
  // Un escandall fill (compost) pot tenir mà d'obra pròpia per dins, que el motor de costos
  // reparteix correctament al total del pare — però no apareix com a línia MO directa aquí.
  // Afegim una fila residual (cost i hores) perquè el desglose per grup sempre sumi el
  // Cost Treball i el Total Hores reals.
  const directLaborCostRaw = directLaborGroups.reduce((s, g) => s + g.cost, 0);
  const directLaborHours   = directLaborGroups.reduce((s, g) => s + g.hours, 0);
  const costHoresRaw       = formData.cachedTotalLaborOwnCost + formData.cachedTotalLaborSubcontractCost;
  const hoursRaw           = formData.cachedTotalHoursOwn + formData.cachedTotalHoursSubcontract;
  const hiddenLaborCostRaw = costHoresRaw - directLaborCostRaw;
  const hiddenLaborHours   = hoursRaw - directLaborHours;
  const laborGroups = (() => {
    if (Math.abs(hiddenLaborCostRaw) <= 0.005) return directLaborGroups;
    const prodLabel = 'Operari taller producció';
    const existing = directLaborGroups.find((g) => g.label === prodLabel);
    if (existing) {
      return directLaborGroups.map((g) =>
        g === existing
          ? { ...g, hours: g.hours + hiddenLaborHours, cost: g.cost + hiddenLaborCostRaw }
          : g,
      );
    }
    return [...directLaborGroups, { label: prodLabel, hours: hiddenLaborHours, cost: hiddenLaborCostRaw }];
  })();
  const costHores     = laborGroups.reduce((s, g) => s + g.cost, 0);
  const totalHoresMO  = laborGroups.reduce((s, g) => s + g.hours, 0);
  const valorAfegit   = baseImposable - costCompres;
  const costTotalDE   = formData.cachedTotalCost;
  const margeBrut     = baseImposable - costTotalDE;
  const factorMarge   = costTotalDE > 0 ? baseImposable / costTotalDE : 0;
  const preuHora      = totalHoresMO > 0 ? valorAfegit / totalHoresMO : 0;
  const biPerKg        = formData.cachedTotalWeightKg > 0 ? baseImposable / formData.cachedTotalWeightKg : 0;

  // Gràfic circular Dades Econòmiques: porcions de cost pur, sense solapament.
  const pieData = [
    { name: 'Cost Materials',       value: Math.max(costMaterials, 0),                    color: '#f87171' },
    { name: 'Cost Serveis',         value: Math.max(costServeis, 0),                       color: '#fb923c' },
    { name: 'Cost Hores',           value: Math.max(directLaborCostRaw, 0),                color: '#8b5cf6' },
    { name: 'Cost Hores Producció', value: Math.max(hiddenLaborCostRaw, 0),                color: '#f59e0b' },
    { name: 'Cost Indirecte',       value: Math.max(formData.cachedTotalIndirectCost, 0), color: '#64748b' },
    { name: 'Marge Brut',           value: Math.max(margeBrut, 0),                        color: '#1d4ed8' },
  ].filter((d) => d.value > 0);

  const [activeTab, setActiveTab] = useState<'dades' | 'descompost' | 'instruccions' | 'documents'>('dades');
  const [chapterDrafts, setChapterDrafts] = useState<Record<number, string>>({});
  const [chapterSuggestIdx, setChapterSuggestIdx] = useState<number | null>(null);
  const [saving, setSaving]           = useState(false);
  const [recalculating, setRecalc]    = useState(false);
  const [loading, setLoading]         = useState(!isNew);
  const [error, setError]             = useState<string | null>(null);
  // true once we have cached totals from backend (existing node loaded, after save, or after Recalcular)
  const [hasBackendData, setHasBackendData] = useState(!isNew);

  // Documents adjunts a la fitxa de l'escandall
  const [documents, setDocuments]     = useState<NodeDocumentRow[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docsDragOver, setDocsDragOver] = useState(false);

  // Reference data
  const [unitTypes, setUnitTypes]           = useState<UnitType[]>([]);
  const [nodeTypes, setNodeTypes]           = useState<NodeTypeOption[]>([]);
  const [elementTypes, setElementTypes]     = useState<ElementTypeOption[]>([]);

  // Importar com a plantilla (nomes disponible quan el Tipus d'Escandall es
  // "Escandall projecte"): copia totes les linies d'un altre Escandall/Compost
  // existent dins d'aquest.
  const [templatePopupOpen, setTemplatePopupOpen] = useState(false);
  const [templateSearch, setTemplateSearch]       = useState('');
  const [templateNodes, setTemplateNodes]         = useState<NodeSummary[]>([]);
  const [importingTemplate, setImportingTemplate] = useState(false);

  // Cell-focus draft pattern (same as CompositeMaterialForm)
  const [cellFocus, setCellFocus] = useState<Record<string, { editing: boolean; draft: string }>>({});

  // Enter key navigates to next focusable cell (input/select) in the Descompost table
  const tableRef = useRef<HTMLTableElement>(null);
  const handleEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.currentTarget.blur();
    const all = Array.from(
      tableRef.current?.querySelectorAll<HTMLElement>('input:not([disabled]), select:not([disabled])') ?? []
    );
    const i = all.indexOf(e.currentTarget);
    if (i !== -1 && i + 1 < all.length) all[i + 1].focus();
  };

  // Drag & drop state
  const pendingDragFromHandleIdx = useRef<number | null>(null);
  const [dragFromIdx, setDragFromIdx] = useState<number | null>(null);
  const [dropTarget, setDropTarget]   = useState<string | null>(null); // "3" | "chapter:Suport"
  const [dirty, setDirty]             = useState(false);

  // Drag & drop de les Instruccions (llista simple, sense capítols agrupats)
  const [dragInstructionIdx, setDragInstructionIdx] = useState<number | null>(null);
  const [dropInstructionTarget, setDropInstructionTarget] = useState<number | null>(null);
  const handleInstructionDragStart = (idx: number) => setDragInstructionIdx(idx);
  const handleInstructionDragEnd = () => { setDragInstructionIdx(null); setDropInstructionTarget(null); };
  const handleInstructionDrop = (toIdx: number) => {
    if (dragInstructionIdx !== null && dragInstructionIdx !== toIdx) {
      setInstructions((prev) => arrayMove(prev, dragInstructionIdx, toIdx));
      setDirty(true);
    }
    setDragInstructionIdx(null);
    setDropInstructionTarget(null);
  };
  const addInstruction = () => setInstructions((prev) => [...prev, { chapter: '', comment: '', photoUrl: null }]);
  const removeInstruction = (idx: number) => setInstructions((prev) => prev.filter((_, i) => i !== idx));
  const updateInstruction = (idx: number, field: 'chapter' | 'comment', value: string) => {
    setInstructions((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };

  // Foto per línia d'instrucció (miniatura ampliable) — reutilitza el mateix
  // endpoint de pujada que la imatge principal de l'escandall.
  const [uploadingInstructionIdx, setUploadingInstructionIdx] = useState<number | null>(null);
  const [zoomInstructionUrl, setZoomInstructionUrl] = useState<string | null>(null);
  const handleInstructionPhotoUpload = async (idx: number, file: File) => {
    setUploadingInstructionIdx(idx);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post<{ success: boolean; data: { imageUrl: string } }>(
        '/nodes/upload-image',
        fd,
        { headers: { 'Content-Type': undefined } },
      );
      setInstructions((prev) => prev.map((row, i) => (i === idx ? { ...row, photoUrl: res.data.data.imageUrl } : row)));
      setDirty(true);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Error en pujar la foto');
    } finally {
      setUploadingInstructionIdx(null);
    }
  };
  const clearInstructionPhoto = (idx: number) => {
    setInstructions((prev) => prev.map((row, i) => (i === idx ? { ...row, photoUrl: null } : row)));
    setDirty(true);
  };

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [lightboxOpen, setLightboxOpen]     = useState(false);
  const [imageDragOver, setImageDragOver]   = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Dropbox Chooser
  const [dropboxReady, setDropboxReady] = useState(false);

  // Autocomplete state — la cerca combina materials i escandalls indistintament
  const [materialSuggestions, setMaterialSuggestions] = useState<SimpleMaterial[]>([]);
  const [nodeSuggestions, setNodeSuggestions]         = useState<NodeSummary[]>([]);
  const [activeSearch, setActiveSearch]        = useState<{ lineIndex: number } | null>(null);
  const [searching, setSearching]             = useState(false);
  const [dropdownRect, setDropdownRect]       = useState<DOMRect | null>(null);
  const inputRefs = useRef<Map<number, HTMLInputElement>>(new Map());

  useEffect(() => {
    api.get<{ success: boolean; data: { appKey: string } }>('/admin/settings/dropbox')
      .then((r) => {
        const key = r.data?.appKey;
        if (!key) return;
        if (document.getElementById('dropboxjs')) { setDropboxReady(true); return; }
        const script = document.createElement('script');
        script.id = 'dropboxjs';
        script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
        script.setAttribute('data-app-key', key);
        script.onload = () => setDropboxReady(true);
        document.head.appendChild(script);
      })
      .catch(() => {});
  }, []);

  const handleDropboxChoose = () => {
    if (!window.Dropbox) return;
    window.Dropbox.choose({
      success: (files) => {
        const file = files[0];
        setFormData(prev => ({ ...prev, folderPath: JSON.stringify({ name: file.name, link: file.link }) }));
      },
      linkType: 'preview',
      multiselect: false,
      folderselect: true,
    });
  };

  // ── Reference data ──────────────────────────────────────────────────────────

  const fetchRefData = useCallback(async () => {
    try {
      const [ccRes, utRes, ntRes, etRes, subRes, prodRes, modelRes, variantRes] = await Promise.all([
        api.get<{ success: boolean; data: SelectOption[] }>('/nodes/composite-codes'),
        api.get<{ success: boolean; data: UnitType[] }>('/nodes/unit-types'),
        api.get<{ success: boolean; data: NodeTypeOption[] }>('/catalog/node-types'),
        api.get<{ success: boolean; data: ElementTypeOption[] }>('/simple-materials/element-types'),
        api.get<{ success: boolean; data: typeof compositeSubfamilies }>('/catalog/composite-subfamilies'),
        api.get<{ success: boolean; data: typeof compositeProducts }>('/catalog/composite-products'),
        api.get<{ success: boolean; data: typeof compositeModels }>('/catalog/composite-models'),
        api.get<{ success: boolean; data: typeof compositeVariants }>('/catalog/composite-variants'),
      ]);
      setCompositeCodes(ccRes.data ?? []);
      setUnitTypes(utRes.data ?? []);
      setNodeTypes((ntRes.data ?? []).filter((nt) => nt.isActive !== 0));
      setElementTypes(etRes.data ?? []);
      setCompositeSubfamilies((subRes.data ?? []).filter((s) => Number(s.isActive) === 1));
      setCompositeProducts((prodRes.data ?? []).filter((p) => Number(p.isActive) === 1));
      setCompositeModels((modelRes.data ?? []).filter((m) => Number(m.isActive) === 1));
      setCompositeVariants((variantRes.data ?? []).filter((v) => Number(v.isActive) === 1));
    } catch { /* non-critical */ }
  }, []);

  // ── Load node (edit mode) ──────────────────────────────────────────────────

  const fetchNode = useCallback(async (nodeId: string) => {
    try {
      setLoading(true);
      const res = await api.get<{ success: boolean; data: any }>(`/nodes/${nodeId}`);
      const n = res.data;
      setFormData({
        code:                        n.code ?? '',
        name:                        n.name ?? '',
        description:                 n.description ?? '',
        observations:                n.observations ?? '',
        nodeType:                    n.nodeType ?? 'COMPOSITE',
        unit:                        n.unit ?? 'Ut',
        amidament:                   n.amidament != null ? Number(n.amidament) : 1,
        compositeCodeId:             n.compositeCodeId,
        compositeSubfamilyId:        n.compositeSubfamilyId,
        compositeProductId:          n.compositeProductId,
        compositeModelId:            n.compositeModelId,
        compositeVariantId:          n.compositeVariantId,
        location:                    n.location ?? '',
        folderPath:                  n.folderPath ?? '',
        planReference:               n.planReference ?? '',
        nodeDate:                    n.nodeDate ? String(n.nodeDate).split('T')[0] : '',
        isSemiFinished:              Number(n.isSemiFinished ?? 0),
        indirectCostPercentage:      Number(n.indirectCostPercentage ?? 5),
        coefVenda:                   n.coefVenda != null ? Number(n.coefVenda) : undefined,
        preuVenda:                   n.preuVenda != null ? Number(n.preuVenda) : undefined,
        // Normalitzar: convertir qualsevol format anterior al format query-param actual
        // Format actual:  /api/v1/nodes/image?cid=1&f=uuid
        // Format vell:    /api/v1/nodes/image/1/uuid.jpg  (o sense extensió)
        imageUrl:                    (() => {
          const u = n.imageUrl;
          if (!u) return null;
          // Ja és el format nou (query params)
          if (u.includes('/api/v1/nodes/image?')) return u;
          // Format vell: /api/v1/nodes/image/:cid/:uuid[.ext]
          const m = u.match(/\/api\/v1\/nodes\/image\/(\d+)\/([^/?]+)/);
          if (m) {
            const cid  = m[1];
            const uuid = m[2].replace(/\.[^.]+$/, '');
            return `/api/v1/nodes/image?cid=${cid}&f=${uuid}`;
          }
          return null;
        })(),
        cachedTotalMaterialsCost:        Number(n.cachedTotalMaterialsCost ?? 0),
        cachedTotalLaborOwnCost:         Number(n.cachedTotalLaborOwnCost ?? 0),
        cachedTotalLaborSubcontractCost: Number(n.cachedTotalLaborSubcontractCost ?? 0),
        cachedTotalServicesCost:         Number(n.cachedTotalServicesCost ?? 0),
        cachedTotalOtherCost:            Number(n.cachedTotalOtherCost ?? 0),
        cachedTotalDirectCost:           Number(n.cachedTotalDirectCost ?? 0),
        cachedTotalIndirectCost:         Number(n.cachedTotalIndirectCost ?? 0),
        cachedTotalCost:                 Number(n.cachedTotalCost ?? 0),
        cachedTotalHoursOwn:             Number(n.cachedTotalHoursOwn ?? 0),
        cachedTotalHoursSubcontract:     Number(n.cachedTotalHoursSubcontract ?? 0),
        cachedTotalHours:                Number(n.cachedTotalHours ?? 0),
        cachedTotalWeightKg:             Number(n.cachedTotalWeightKg ?? 0),
        cachedCostPerKg:                 Number(n.cachedCostPerKg ?? 0),
      });
      const loadedLines: NodeFormLine[] = (n.lines ?? []).map((l: any): NodeFormLine => ({
        id:               l.id,
        childType:        l.childType,
        childMaterialId:  l.childMaterialId ?? null,
        childNodeId:      l.childNodeId ?? null,
        compositeCodeId:  l.compositeCodeId ?? null,
        elementImageUrl:  l.elementImageUrl ?? null,
        // description is already the item name (set when the line was created)
        description:      l.description ?? '',
        nature:           l.nature ?? 'MATERIAL',
        unit:             l.unit ?? 'Ut',
        quantity:         Number(l.quantity ?? 1),
        dimensionA:       Number(l.dimensionA ?? 0),
        dimensionB:       Number(l.dimensionB ?? 0),
        dimensionH:       Number(l.dimensionH ?? 0),
        measurement:      Number(l.measurement ?? 0),
        isPriceLocked:    Number(l.isPriceLocked ?? 0),
        unitCostOverride: l.unitCostOverride != null ? Number(l.unitCostOverride) : null,
        resolvedUnitCost: Number(l.resolvedUnitCost ?? 0),
        lineTotal:        Number(l.lineTotal ?? 0),
        hoursProduction:  Number(l.hoursProduction ?? 0),
        hoursFabrication: Number(l.hoursFabrication ?? 0),
        hoursAssembly:    Number(l.hoursAssembly ?? 0),
        weightKg:         Number(l.weightKg ?? 0),
        referencia:       l.referencia ?? '',
        chapter:          l.chapter ?? '',
        position:         l.position ?? '',
        detail:           l.detail ?? '',
        marge:            l.marge != null ? Number(l.marge) : null,
        descompte:        l.descompte != null ? Number(l.descompte) : null,
        observations:     l.observations ?? '',
      }));
      setLines(loadedLines);
      const loadedInstructions: NodeInstructionRow[] = (n.instructions ?? []).map((i: any): NodeInstructionRow => ({
        id:       i.id,
        chapter:  i.chapter ?? '',
        comment:  i.comment ?? '',
        photoUrl: i.photoUrl ?? null,
      }));
      setInstructions(loadedInstructions);
      setHasBackendData(true);
    } catch {
      setError('Error en carregar el node');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRefData();
    if (id) fetchNode(id);
  }, [fetchRefData, fetchNode, id]);

  // ── Documents adjunts a la fitxa de l'escandall ──────────────────────────

  const fetchDocuments = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get<{ success: boolean; data: NodeDocumentRow[] }>(`/nodes/${id}/documents`);
      setDocuments(res.data ?? []);
    } catch { /* no bloquejar la resta del formulari si falla el llistat */ }
  }, [id]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleUploadDocument = async (file: File) => {
    if (!id || !file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      await apiClient.post(`/nodes/${id}/documents`, fd, { headers: { 'Content-Type': undefined } });
      fetchDocuments();
    } catch (err: any) {
      alert('Error pujant el document: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocumentsDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDocsDragOver(false);
    Array.from(e.dataTransfer.files ?? []).forEach((f) => handleUploadDocument(f));
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!id) return;
    if (!confirm('Eliminar aquest document?')) return;
    try {
      await api.delete(`/nodes/${id}/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch { alert('Error eliminant el document'); }
  };

  // Les rutes de documents requereixen el token d'autenticació (Authorization
  // header), que un <a href> o window.open directe no envia — es baixa com a
  // blob i s'obre/descarrega des d'aquí.
  const handleOpenDocument = async (doc: NodeDocumentRow, download: boolean) => {
    try {
      const res = await apiClient.get(`/nodes/${id}/documents/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.mimeType || 'application/octet-stream' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.name;
        a.click();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert('Error obrint el document');
    }
  };

  const fmtFileSize = (bytes: number) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  useEffect(() => {
    const reset = () => { pendingDragFromHandleIdx.current = null; };
    document.addEventListener('pointerup', reset);
    return () => document.removeEventListener('pointerup', reset);
  }, []);


  // ── Line mutations ─────────────────────────────────────────────────────────

  const updateLine = (index: number, field: keyof NodeFormLine, value: any) => {
    setLines((prev) => {
      const updated = [...prev];
      const line    = { ...updated[index], [field]: value };
      updated[index] = applyLineTotals(line);
      return updated;
    });
  };

  const addLine = (childType: ChildType) => {
    const base = childType === 'MATERIAL' ? emptyMaterialLine() : emptyNodeLine();
    setLines((prev) => [...prev, { ...base, chapter: '', marge: null }]);
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Drag & drop ────────────────────────────────────────────────────────────

  const handleDragStart = (e: React.DragEvent, idx: number) => {
    if (pendingDragFromHandleIdx.current !== idx) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
    setDragFromIdx(idx);
  };

  const handleDragEnd = () => {
    pendingDragFromHandleIdx.current = null;
    setDragFromIdx(null);
    setDropTarget(null);
  };

  const handleDropOnLine = (e: React.DragEvent, toIdx: number) => {
    e.preventDefault();
    if (dragFromIdx === null) { handleDragEnd(); return; }
    const from = dragFromIdx;
    const targetChapter = lines[toIdx]?.chapter ?? '';
    setLines((prev) => {
      const updated = prev.map((l, i) => i === from ? { ...l, chapter: targetChapter } : l);
      return from === toIdx ? updated : arrayMove(updated, from, toIdx);
    });
    setDirty(true);
    handleDragEnd();
  };

  const handleDropOnChapter = (e: React.DragEvent, chapName: string) => {
    e.preventDefault();
    if (dragFromIdx === null) { handleDragEnd(); return; }
    const from = dragFromIdx;
    const { SENSE_CHAPTER } = { SENSE_CHAPTER: 'Sense capítol' };
    const targetChapter = chapName === SENSE_CHAPTER ? '' : chapName;
    setLines((prev) => {
      const updated = prev.map((l, i) => i === from ? { ...l, chapter: targetChapter } : l);
      // Insert at position of first line belonging to the target chapter (excluding the dragged item)
      const firstInChap = updated.findIndex((l, i) => i !== from && (l.chapter?.trim() || SENSE_CHAPTER) === chapName);
      if (firstInChap >= 0 && firstInChap !== from) {
        return arrayMove(updated, from, firstInChap);
      }
      return updated;
    });
    setDirty(true);
    handleDragEnd();
  };

  // ── Autocomplete ────────────────────────────────────────────────────────────

  const searchItems = async (query: string, lineIndex: number) => {
    if (query.length < 2) {
      setMaterialSuggestions([]);
      setNodeSuggestions([]);
      setActiveSearch(null);
      return;
    }
    setActiveSearch({ lineIndex });
    setSearching(true);
    try {
      const [matRes, nodeRes] = await Promise.all([
        api.get<{ success: boolean; data: SimpleMaterial[] }>(
          `/simple-materials/search?q=${encodeURIComponent(query)}&limit=10`,
        ),
        api.get<{ success: boolean; data: NodeSummary[] }>(
          '/nodes', { search: query, limit: 50 } as Record<string, unknown>,
        ),
      ]);
      setMaterialSuggestions(matRes.data ?? []);
      // Filter out the node being edited (prevents self-reference cycles)
      setNodeSuggestions((nodeRes.data ?? []).filter((n: NodeSummary) => n.id !== id));
    } catch {
      setMaterialSuggestions([]);
      setNodeSuggestions([]);
    } finally {
      setSearching(false);
    }
  };

  const selectMaterial = (mat: SimpleMaterial, lineIndex: number) => {
    setLines((prev) => {
      const updated = [...prev];
      const et     = mat.elementTypeId != null ? elementTypes.find((e) => e.id === mat.elementTypeId) : undefined;
      const nature = natureForElementType(et) ?? updated[lineIndex].nature;
      // La mà d'obra sempre es mesura en Hores, independentment de la unitat pròpia del
      // material al catàleg (p.ex. si allà està configurada com a "D" — dia).
      const isLabor = nature === 'MO_PROPIA' || nature === 'MO_SUBCONTRACT';
      const line    = {
        ...updated[lineIndex],
        childType:       'MATERIAL' as ChildType,
        description:     mat.name,
        childMaterialId: mat.id,
        childNodeId:     null,
        compositeCodeId: mat.compositeCodeId ?? null,
        unit:            isLabor ? 'H' : mat.unit,
        weightKg:        mat.weightKgPerUnit,
        // Preu final (€/unitat de mesura), mateixa fórmula que "Preu/{unitat}" a la ficha del
        // material — no costPerUnit (basat en pes, dona 0 en materials sense pes com hores de personal).
        resolvedUnitCost: Number(mat.pricePerMagnitude ?? mat.costPerUnit ?? 0),
        nature,
        elementImageUrl: mat.technicalImageUrl ?? null,
      };
      updated[lineIndex] = applyLineTotals(line);
      return updated;
    });
    clearAutocomplete();
  };

  const selectNode = (node: NodeSummary, lineIndex: number) => {
    setLines((prev) => {
      const updated = [...prev];
      const line    = {
        ...updated[lineIndex],
        childType:        'NODE' as ChildType,
        description:      node.name,
        childNodeId:      node.id,
        childMaterialId:  null,
        compositeCodeId:  node.compositeCodeId ?? null,
        unit:             node.unit,
        // cost direct del fill — lectura; el backend ho confirmarà en desar
        resolvedUnitCost: Number(node.cachedTotalDirectCost || 0),
        // els nodes fills mai porten preu bloquejat
        isPriceLocked:    0,
        unitCostOverride: null,
      };
      updated[lineIndex] = applyLineTotals(line);
      return updated;
    });
    clearAutocomplete();
  };

  const clearAutocomplete = () => {
    setMaterialSuggestions([]);
    setNodeSuggestions([]);
    setActiveSearch(null);
    setDropdownRect(null);
  };

  // ── Importar com a plantilla (nomes "Escandall projecte") ──────────────────

  const openTemplatePopup = async () => {
    setTemplatePopupOpen(true);
    setTemplateSearch('');
    try {
      const res = await api.get<{ success: boolean; data: NodeSummary[] }>('/nodes', { limit: 500, isActive: 1 } as Record<string, unknown>);
      setTemplateNodes((res.data ?? []).filter((n) => n.id !== id));
    } catch {
      setTemplateNodes([]);
    }
  };

  const importTemplate = async (sourceNode: NodeSummary) => {
    if (lines.length > 0 && !confirm(`Ja hi ha línies en aquest escandall. Vols reemplaçar-les per les de "${sourceNode.name}"?`)) return;
    setImportingTemplate(true);
    try {
      const res = await api.get<{ success: boolean; data: any }>(`/nodes/${sourceNode.id}`);
      const n = res.data;

      // Importa tambe les dades de capçalera de la plantilla, excepte el codi
      // (es manté el propi de l'escandall de projecte) i el Tipus d'Escandall
      // (es manté "Projecte", que és el que permet aquesta importació).
      setFormData((prev) => ({
        ...prev,
        name:                    n.name ?? prev.name,
        description:             n.description ?? prev.description,
        observations:            n.observations ?? prev.observations,
        imageUrl:                n.imageUrl ?? prev.imageUrl,
        unit:                    n.unit ?? prev.unit,
        amidament:               n.amidament != null ? Number(n.amidament) : prev.amidament,
        compositeCodeId:         n.compositeCodeId ?? prev.compositeCodeId,
        compositeSubfamilyId:    n.compositeSubfamilyId ?? prev.compositeSubfamilyId,
        compositeProductId:      n.compositeProductId ?? prev.compositeProductId,
        compositeModelId:        n.compositeModelId ?? prev.compositeModelId,
        compositeVariantId:      n.compositeVariantId ?? prev.compositeVariantId,
        location:                n.location ?? prev.location,
        folderPath:              n.folderPath ?? prev.folderPath,
        planReference:           n.planReference ?? prev.planReference,
        nodeDate:                n.nodeDate ? String(n.nodeDate).split('T')[0] : prev.nodeDate,
        isSemiFinished:          Number(n.isSemiFinished ?? prev.isSemiFinished),
        indirectCostPercentage:  n.indirectCostPercentage != null ? Number(n.indirectCostPercentage) : prev.indirectCostPercentage,
        coefVenda:               n.coefVenda != null ? Number(n.coefVenda) : prev.coefVenda,
        preuVenda:               n.preuVenda != null ? Number(n.preuVenda) : prev.preuVenda,
      }));

      const sourceLines: any[] = n.lines ?? [];
      const newLines: NodeFormLine[] = sourceLines.map((l): NodeFormLine => ({
        childType:        l.childType,
        childMaterialId:  l.childMaterialId ?? null,
        childNodeId:      l.childNodeId ?? null,
        compositeCodeId:  l.compositeCodeId ?? null,
        elementImageUrl:  l.elementImageUrl ?? null,
        description:      l.description ?? '',
        nature:           l.nature ?? 'MATERIAL',
        unit:             l.unit ?? 'Ut',
        quantity:         Number(l.quantity ?? 1),
        dimensionA:       Number(l.dimensionA ?? 0),
        dimensionB:       Number(l.dimensionB ?? 0),
        dimensionH:       Number(l.dimensionH ?? 0),
        measurement:      Number(l.measurement ?? 0),
        isPriceLocked:    Number(l.isPriceLocked ?? 0),
        unitCostOverride: l.unitCostOverride != null ? Number(l.unitCostOverride) : null,
        resolvedUnitCost: Number(l.resolvedUnitCost ?? 0),
        lineTotal:        Number(l.lineTotal ?? 0),
        hoursProduction:  Number(l.hoursProduction ?? 0),
        hoursFabrication: Number(l.hoursFabrication ?? 0),
        hoursAssembly:    Number(l.hoursAssembly ?? 0),
        weightKg:         Number(l.weightKg ?? 0),
        referencia:       l.referencia ?? '',
        chapter:          l.chapter ?? '',
        position:         l.position ?? '',
        detail:           l.detail ?? '',
        marge:            l.marge != null ? Number(l.marge) : null,
        descompte:        l.descompte != null ? Number(l.descompte) : null,
        observations:     l.observations ?? '',
        // id s'omet expressament: son linies noves d'aquest escandall, no una
        // referencia a les linies originals de la plantilla.
      }));
      setLines(newLines);
      setDirty(true);
    } catch {
      setError('Error en importar la plantilla');
    } finally {
      setImportingTemplate(false);
      setTemplatePopupOpen(false);
    }
  };

  // ── Navigation ─────────────────────────────────────────────────────────────

  const goBack = () => {
    localStorage.setItem('compras-activeTab', 'nodes');
    navigate('/compres');
  };

  // ── Imatge ────────────────────────────────────────────────────────────────

  const handleImageUpload = async (file: File) => {
    if (!file) return;
    setUploadingImage(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Content-Type: undefined → el navegador posa el boundary multipart correctament
      // (el default 'application/json' del apiClient trencaria multer)
      const res = await apiClient.post<{ success: boolean; data: { imageUrl: string } }>(
        '/nodes/upload-image',
        fd,
        { headers: { 'Content-Type': undefined } },
      );
      setFormData((prev) => ({ ...prev, imageUrl: res.data.data.imageUrl }));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Error en pujar la imatge');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setImageDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleImageUpload(file);
  };

  // ── Recalcular (preview sense guardar) ────────────────────────────────────

  const handlePreview = async () => {
    if (lines.length === 0) return;
    setRecalc(true);
    setError(null);
    try {
      const payload = {
        indirectCostPercentage: formData.indirectCostPercentage,
        lines: lines.map((l, i) => ({
          childType:        l.childType,
          childMaterialId:  l.childType === 'MATERIAL' ? (l.childMaterialId ?? undefined) : undefined,
          childNodeId:      l.childType === 'NODE'     ? (l.childNodeId     ?? undefined) : undefined,
          nature:           l.nature,
          quantity:         l.quantity,
          dimensionA:       l.dimensionA,
          dimensionB:       l.dimensionB,
          dimensionH:       l.dimensionH,
          isPriceLocked:    l.isPriceLocked,
          unitCostOverride: l.isPriceLocked ? (l.unitCostOverride ?? undefined) : undefined,
          hoursProduction:  l.hoursProduction,
          hoursFabrication: l.hoursFabrication,
          hoursAssembly:    l.hoursAssembly,
          weightKg:         l.weightKg,
          sortOrder:        i,
        })),
      };

      const res = await api.post<{ success: boolean; data: any }>(
        '/nodes/calculate-preview',
        payload,
      );
      const d = res?.data;
      if (!d) return;

      // Actualitza el panel de totals amb el desglose del backend
      setFormData((prev) => ({
        ...prev,
        cachedTotalMaterialsCost:        Number(d.totalMaterialsCost        ?? 0),
        cachedTotalLaborOwnCost:         Number(d.totalLaborOwnCost         ?? 0),
        cachedTotalLaborSubcontractCost: Number(d.totalLaborSubcontractCost ?? 0),
        cachedTotalServicesCost:         Number(d.totalServicesCost         ?? 0),
        cachedTotalOtherCost:            Number(d.totalOtherCost            ?? 0),
        cachedTotalDirectCost:           Number(d.totalDirectCost           ?? 0),
        cachedTotalIndirectCost:         Number(d.totalIndirectCost         ?? 0),
        cachedTotalCost:                 Number(d.totalCost                 ?? 0),
        cachedTotalHoursOwn:             Number(d.totalHoursOwn             ?? 0),
        cachedTotalHoursSubcontract:     Number(d.totalHoursSubcontract     ?? 0),
        cachedTotalHours:                Number(d.totalHours                ?? 0),
        cachedTotalWeightKg:             Number(d.totalWeightKg             ?? 0),
        cachedCostPerKg:                 Number(d.costPerKg                 ?? 0),
      }));
      setHasBackendData(true);

      // Actualitza measurement, resolvedUnitCost i lineTotal de cada línia amb els resultats del motor
      if (Array.isArray(d.lineResults) && d.lineResults.length === lines.length) {
        setLines((prev) =>
          prev.map((line, i) => {
            const lr = d.lineResults[i];
            if (!lr) return line;
            return {
              ...line,
              measurement:      Number(lr.measurement      ?? line.measurement),
              resolvedUnitCost: Number(lr.resolvedUnitCost ?? line.resolvedUnitCost),
              lineTotal:        Number(lr.lineTotal        ?? line.lineTotal),
            };
          }),
        );
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Error en recalcular');
    } finally {
      setRecalc(false);
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!formData.code.trim()) { setError('El codi és obligatori'); return; }
    if (!formData.name.trim()) { setError('El nom és obligatori'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        code:                   formData.code || undefined,
        name:                   formData.name,
        description:            formData.description || undefined,
        observations:           formData.observations || undefined,
        nodeType:               formData.nodeType,
        unit:                   formData.unit,
        amidament:              formData.amidament || 1,
        compositeCodeId:        formData.compositeCodeId || undefined,
        compositeSubfamilyId:   formData.compositeSubfamilyId || undefined,
        compositeProductId:     formData.compositeProductId || undefined,
        compositeModelId:       formData.compositeModelId || undefined,
        compositeVariantId:     formData.compositeVariantId || undefined,
        location:               formData.location || undefined,
        folderPath:             formData.folderPath || undefined,
        planReference:          formData.planReference || undefined,
        nodeDate:               formData.nodeDate || undefined,
        isSemiFinished:         formData.isSemiFinished,
        indirectCostPercentage: formData.indirectCostPercentage,
        coefVenda:              formData.nodeType === 'ESCANDALL' ? (formData.coefVenda || undefined) : undefined,
        // preuVenda (suma d'Import de les linies) es desa per a tots els tipus
        // — un Compost tambe te el seu propi Ingrés Total/Factor Marge/Preu hora
        // a la pestanya Dades, i el llistat d'Escandalls (Nodes.tsx) els necessita
        // igual que per a un Escandall.
        preuVenda:              totalImport || undefined,
        imageUrl:               formData.imageUrl,
        lines: lines.map((l, i) => ({
          childType:        l.childType,
          childMaterialId:  l.childType === 'MATERIAL' ? l.childMaterialId : undefined,
          childNodeId:      l.childType === 'NODE' ? l.childNodeId : undefined,
          nature:           l.nature,
          description:      l.description || undefined,
          referencia:       l.referencia || undefined,
          chapter:          l.chapter?.trim() || undefined,
          position:         l.position || undefined,
          detail:           l.detail?.trim() || undefined,
          unit:             l.unit,
          quantity:         l.quantity,
          dimensionA:       l.dimensionA,
          dimensionB:       l.dimensionB,
          dimensionH:       l.dimensionH,
          isPriceLocked:    l.isPriceLocked,
          unitCostOverride: l.isPriceLocked ? l.unitCostOverride : undefined,
          hoursProduction:  l.hoursProduction,
          hoursFabrication: l.hoursFabrication,
          hoursAssembly:    l.hoursAssembly,
          weightKg:         l.weightKg,
          marge:            l.marge ?? undefined,
          descompte:        l.descompte ?? undefined,
          observations:     l.observations?.trim() || undefined,
          sortOrder:        i,
        })),
        instructions: instructions.map((row, i) => ({
          chapter:   row.chapter?.trim() || undefined,
          comment:   row.comment?.trim() || undefined,
          photoUrl:  row.photoUrl || undefined,
          sortOrder: i,
        })),
      };

      let saved: any;
      if (isNew) {
        saved = await api.post<{ success: boolean; data: any }>('/nodes', payload);
      } else {
        saved = await api.put<{ success: boolean; data: any }>(`/nodes/${id}`, payload);
      }

      // Refresh cached totals from backend response
      const n = saved?.data;
      if (n) {
        setFormData((prev) => ({
          ...prev,
          cachedTotalMaterialsCost:        Number(n.cachedTotalMaterialsCost ?? 0),
          cachedTotalLaborOwnCost:         Number(n.cachedTotalLaborOwnCost ?? 0),
          cachedTotalLaborSubcontractCost: Number(n.cachedTotalLaborSubcontractCost ?? 0),
          cachedTotalServicesCost:         Number(n.cachedTotalServicesCost ?? 0),
          cachedTotalOtherCost:            Number(n.cachedTotalOtherCost ?? 0),
          cachedTotalDirectCost:           Number(n.cachedTotalDirectCost ?? 0),
          cachedTotalIndirectCost:         Number(n.cachedTotalIndirectCost ?? 0),
          cachedTotalCost:                 Number(n.cachedTotalCost ?? 0),
          cachedTotalHoursOwn:             Number(n.cachedTotalHoursOwn ?? 0),
          cachedTotalHoursSubcontract:     Number(n.cachedTotalHoursSubcontract ?? 0),
          cachedTotalHours:                Number(n.cachedTotalHours ?? 0),
          cachedTotalWeightKg:             Number(n.cachedTotalWeightKg ?? 0),
          cachedCostPerKg:                 Number(n.cachedCostPerKg ?? 0),
        }));
        setHasBackendData(true);
      }
      goBack();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Error en guardar');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // La importacio "com a plantilla" nomes es permet quan el Tipus d'Escandall
  // actual es "Escandall projecte" (catàleg node_types, gestionat des de
  // l'admin — es compara pel nom, no per un codi fix).
  const isProjecteType = (nodeTypes.find((nt) => nt.code === formData.nodeType)?.name ?? '').trim().toLowerCase() === 'projecte';

  if (loading) return <div className="p-8 text-center">Carregant...</div>;

  return (
    <div className="space-y-4">

      {/* Capçalera + pestanyes: sticky com un sol bloc, sempre visibles en fer scroll */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b flex flex-col gap-3 py-3 -mx-4 px-4 md:-mx-6 md:px-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={goBack}
              className="p-2 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-foreground truncate">
                {isNew ? 'Nou Escandall' : (formData.name || 'Editar Escandall')}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button type="button" onClick={goBack} className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50">Cancel·lar</button>
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={recalculating || lines.length === 0}
              title="Calcula els totals en viu sense guardar"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
              {recalculating ? 'Recalculant...' : 'Recalcular'}
            </Button>
            {dirty && (
              <span className="text-amber-600 text-xs font-normal self-center">· ordre sense desar</span>
            )}
            <button type="button" onClick={handleSave} disabled={saving} className={`flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50${dirty ? ' ring-2 ring-amber-400' : ''}`}>
              <Save className="h-4 w-4" />
              {saving ? 'Desant...' : 'Desa'}
            </button>
          </div>
        </div>

        {/* ── Pestanyes Dades / Descompost / Instruccions / Documentació ── */}
        <div className="grid grid-cols-4 gap-3">
          <button
            type="button"
            onClick={() => setActiveTab('dades')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'dades' ? 'border-blue-500 bg-blue-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-blue-500/10 shrink-0"><FileText className="h-4 w-4 text-blue-500" /></div>
            <span className="text-xs font-semibold">Dades</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('descompost')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'descompost' ? 'border-teal-500 bg-teal-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-teal-500/10 shrink-0"><Table2 className="h-4 w-4 text-teal-500" /></div>
            <span className="text-xs font-semibold">Detall</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('instruccions')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'instruccions' ? 'border-amber-500 bg-amber-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-amber-500/10 shrink-0"><ListChecks className="h-4 w-4 text-amber-500" /></div>
            <span className="text-xs font-semibold">Instruccions</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('documents')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'documents' ? 'border-rose-500 bg-rose-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-rose-500/10 shrink-0"><Paperclip className="h-4 w-4 text-rose-500" /></div>
            <span className="text-xs font-semibold">Documentació{documents.length > 0 ? ` (${documents.length})` : ''}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}

      {activeTab === 'dades' && (
      <>
      {/* ── Header fields + Foto + Valors Calculats ── */}
      <Card className="bg-muted/30 [&_input:not(.hidden)]:bg-white [&_select]:bg-white [&_textarea]:bg-white"><CardContent className="p-6">
      <div className="flex flex-col gap-6">

      <div className="flex gap-6 items-stretch">

        {/* Left column — header fields */}
        <div className="flex flex-col gap-4 flex-1 min-w-0">

          {/* Row 1: Codi / Tipus node / Amidament / Unitat / Data */}
          <div className="grid grid-cols-9 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1">Codi *</label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="Ex: 110.13.01"
                className="w-32"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1">Tipus d'Escandall</label>
              <select
                value={formData.nodeType}
                onChange={(e) => setFormData({ ...formData, nodeType: e.target.value as NodeType })}
                className="w-full border rounded px-2 py-2 text-sm"
              >
                {nodeTypes.length > 0
                  ? nodeTypes.map((nt) => <option key={nt.code} value={nt.code}>{nt.name}</option>)
                  : (
                    <>
                      <option value="ESCANDALL">Escandall</option>
                      <option value="COMPOSITE">Compost</option>
                    </>
                  )}
              </select>
            </div>
            <div className="col-span-1">
              <label className="block text-xs font-medium mb-1" title="Magnitud pròpia de l'escandall (p.ex. 7,5 m2), usada per calcular els Valors/UA">Amidament</label>
              <Input
                type="text" inputMode="decimal"
                className="w-full text-right"
                value={cellFocus['header-amidament']?.editing ? cellFocus['header-amidament'].draft : fmt2(formData.amidament)}
                onFocus={() => setCellFocus((p) => ({ ...p, 'header-amidament': { editing: true, draft: String(formData.amidament) } }))}
                onChange={(e) => setCellFocus((p) => ({ ...p, 'header-amidament': { editing: true, draft: e.target.value } }))}
                onBlur={(e) => {
                  const v = e.target.value.replace(',', '.').trim();
                  setFormData((prev) => ({ ...prev, amidament: v === '' ? 1 : (parseFloat(v) || 1) }));
                  setCellFocus((p) => ({ ...p, 'header-amidament': { editing: false, draft: '' } }));
                }}
              />
            </div>
            <div className="col-span-1">
              <label className="block text-xs font-medium mb-1">Unitat</label>
              <select
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full border rounded px-2 py-2 text-sm"
              >
                {unitTypes.length > 0
                  ? unitTypes.map((ut) => <option key={ut.code} value={ut.code}>{ut.code}</option>)
                  : <option value="Ut">Ut</option>}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1">Data</label>
              <div className="flex items-center gap-2">
                <Input type="date" value={formData.nodeDate} onChange={(e) => setFormData({ ...formData, nodeDate: e.target.value })} className="w-40" />
                {isProjecteType && (
                  <Button type="button" size="sm" variant="outline" className="text-[11px] whitespace-nowrap" onClick={openTemplatePopup}>
                    <Download className="h-4 w-4 mr-1" /> Importar com a plantilla
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Nom */}
          <div>
            <label className="block text-xs font-medium mb-1">Nom *</label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
          </div>

          {/* Row 3: Descripció */}
          <div>
            <label className="block text-xs font-medium mb-1">Descripció</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border rounded px-3 py-2 text-sm"
              rows={2}
            />
          </div>

          {/* Row 4: Codi producte / Subfamília / Producte / Model / Variant —
              Família i els 4 nivells en cascada de l'Arbre d'elements, tots a
              la mateixa línia. */}
          <div className="grid grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Codi producte</label>
              <select
                value={formData.compositeCodeId ?? ''}
                onChange={(e) => {
                  const newCompositeCodeId = e.target.value ? parseInt(e.target.value) : undefined;
                  setFormData({
                    ...formData,
                    compositeCodeId: newCompositeCodeId,
                    compositeSubfamilyId: undefined,
                    compositeProductId: undefined,
                    compositeModelId: undefined,
                    compositeVariantId: undefined,
                  });
                }}
                className="w-full border rounded px-2 py-2 text-sm"
              >
                <option value="">Selecciona...</option>
                {compositeCodes.map((cc) => <option key={cc.id} value={cc.id}>{cc.code} - {cc.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Subfamília</label>
              <select
                value={formData.compositeSubfamilyId ?? ''}
                disabled={!formData.compositeCodeId}
                onChange={(e) => {
                  const newSubfamilyId = e.target.value ? parseInt(e.target.value) : undefined;
                  setFormData({ ...formData, compositeSubfamilyId: newSubfamilyId, compositeProductId: undefined, compositeModelId: undefined, compositeVariantId: undefined });
                }}
                className="w-full border rounded px-2 py-2 text-sm"
              >
                <option value="">Selecciona...</option>
                {compositeSubfamilies
                  .filter((s) => !formData.compositeCodeId || s.compositeCodeId === formData.compositeCodeId)
                  .map((s) => <option key={s.id} value={s.id}>{s.code} - {s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Producte</label>
              <select
                value={formData.compositeProductId ?? ''}
                disabled={!formData.compositeSubfamilyId}
                onChange={(e) => {
                  const newProductId = e.target.value ? parseInt(e.target.value) : undefined;
                  setFormData({ ...formData, compositeProductId: newProductId, compositeModelId: undefined, compositeVariantId: undefined });
                }}
                className="w-full border rounded px-2 py-2 text-sm"
              >
                <option value="">Selecciona...</option>
                {compositeProducts
                  .filter((p) => !formData.compositeSubfamilyId || p.subfamilyId === formData.compositeSubfamilyId)
                  .map((p) => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Model</label>
              <select
                value={formData.compositeModelId ?? ''}
                disabled={!formData.compositeProductId}
                onChange={(e) => {
                  const newModelId = e.target.value ? parseInt(e.target.value) : undefined;
                  setFormData({ ...formData, compositeModelId: newModelId, compositeVariantId: undefined });
                }}
                className="w-full border rounded px-2 py-2 text-sm"
              >
                <option value="">Selecciona...</option>
                {compositeModels
                  .filter((m) => !formData.compositeProductId || m.productId === formData.compositeProductId)
                  .map((m) => <option key={m.id} value={m.id}>{m.code}{m.name ? ` - ${m.name}` : ''}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Variant</label>
              <select
                value={formData.compositeVariantId ?? ''}
                disabled={!formData.compositeModelId}
                onChange={(e) => setFormData({ ...formData, compositeVariantId: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full border rounded px-2 py-2 text-sm"
              >
                <option value="">Selecciona...</option>
                {compositeVariants
                  .filter((v) => !formData.compositeModelId || v.modelId === formData.compositeModelId)
                  .map((v) => <option key={v.id} value={v.id}>{v.code}{v.name ? ` - ${v.name}` : ''}</option>)}
              </select>
            </div>
          </div>

          {/* Row 4b: Observacions / Plànol */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Observacions</label>
              <Input value={formData.observations} onChange={(e) => setFormData({ ...formData, observations: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Plànol</label>
              <Input value={formData.planReference} onChange={(e) => setFormData({ ...formData, planReference: e.target.value })} className="w-full" />
            </div>
          </div>

          {/* Row 5: Localització (magatzem) + Carpeta Dropbox, mateixa línia */}
          <div className="flex gap-3 items-end">
            <div className="w-48 shrink-0">
              <label className="block text-xs font-medium mb-1">Localització</label>
              <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Ubicació al magatzem" />
            </div>

            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium mb-1">Carpeta Dropbox</label>
              <div className="flex gap-1.5">
                <div className="flex-1 flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/30 text-xs min-h-[38px] overflow-hidden">
                  {formData.folderPath ? (() => {
                    try {
                      const loc: { name: string; link: string } = JSON.parse(formData.folderPath);
                      return (<><FolderOpen className="h-4 w-4 text-blue-500 shrink-0" /><span className="truncate">{loc.name}</span></>);
                    } catch { return <span className="text-muted-foreground truncate">{formData.folderPath}</span>; }
                  })() : <span className="text-muted-foreground">Cap carpeta seleccionada</span>}
                </div>
                {dropboxReady && (
                  <button type="button" onClick={handleDropboxChoose}
                    className="px-3 py-2 border rounded-lg hover:bg-muted transition-colors text-xs flex items-center gap-1.5 shrink-0">
                    <FolderOpen className="h-4 w-4" />Seleccionar
                  </button>
                )}
                {formData.folderPath && (() => {
                  try {
                    const loc: { name: string; link: string } = JSON.parse(formData.folderPath);
                    return loc.link ? (
                      <a href={loc.link} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-2 border rounded-lg hover:bg-muted transition-colors flex items-center shrink-0" title="Obrir a Dropbox">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : null;
                  } catch { return null; }
                })()}
                {formData.folderPath && (
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, folderPath: '' }))}
                    className="px-2 py-2 border rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-red-500 shrink-0">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Right column — Foto */}
        <div className="w-96 shrink-0 flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); e.target.value = ''; }}
          />
          <label className="block text-xs font-medium text-slate-700">Foto</label>
          {formData.imageUrl ? (
            <div className="relative group rounded border overflow-hidden bg-slate-100 flex-1">
              <img
                src={formData.imageUrl}
                alt="Imatge escandall"
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => setLightboxOpen(true)}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <button type="button" title="Veure a mida completa" onClick={() => setLightboxOpen(true)}
                  className="bg-white/90 rounded-full p-2 hover:bg-white shadow">
                  <ZoomIn className="h-4 w-4 text-slate-700" />
                </button>
                <button type="button" title="Substituir imatge" onClick={() => fileInputRef.current?.click()}
                  className="bg-white/90 rounded-full p-2 hover:bg-white shadow">
                  <Upload className="h-4 w-4 text-slate-700" />
                </button>
                <button type="button" title="Eliminar imatge"
                  onClick={() => setFormData((prev) => ({ ...prev, imageUrl: null }))}
                  className="bg-white/90 rounded-full p-2 hover:bg-white shadow">
                  <X className="h-4 w-4 text-red-600" />
                </button>
              </div>
            </div>
          ) : (
            <div
              className={`border-2 border-dashed rounded flex flex-col items-center justify-center cursor-pointer transition-colors flex-1 ${
                imageDragOver ? 'border-teal-400 bg-teal-50' : 'border-slate-300 hover:border-slate-400 bg-slate-50'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setImageDragOver(true); }}
              onDragLeave={() => setImageDragOver(false)}
              onDrop={handleImageDrop}
            >
              {uploadingImage ? (
                <div className="text-muted-foreground text-xs text-center px-2">Pujant...</div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-slate-300 mb-2" />
                  <div className="text-xs text-muted-foreground text-center px-2">Arrossega o clica per afegir foto</div>
                  <div className="text-[10px] text-muted-foreground mt-1">JPG · PNG · WEBP<br />màx. 10 MB</div>
                </>
              )}
            </div>
          )}
        </div>

      </div>

      {/* Valors Calculats — fila completa, horitzontal */}
      <div className="bg-card border rounded-lg p-5">
        {!hasBackendData ? (
          <div className="text-muted-foreground text-xs text-center py-4 border rounded bg-slate-50">
            Es calcularà en desar.<br />
            <span className="text-xs">(els nodes fills requereixen el motor recursiu del backend)</span>
          </div>
        ) : (
          <div className="grid gap-x-10 gap-y-6 lg:grid-cols-[minmax(380px,1fr)_minmax(460px,1.15fr)_minmax(300px,1.1fr)] 2xl:max-w-[1900px]">
            <>
                {/* Ratis */}
                <div className="lg:border-r lg:pr-10">
                  <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 mb-2 border-b">Ratis</h5>
                  <ValorsRow label="Factor Marge (BI/CT)" value={fmt2(factorMarge)} note="> 1,50" bold />
                  <ValorsRow label="Preu hora" value={fmt2(preuHora)} unit="€/h" note="> 45 €/h" bold />
                  <div className="border-t border-dashed my-2" />
                  {laborGroups.map((g) => (
                    <ValorsRow
                      key={g.label}
                      label={g.label}
                      value={fmt2(g.hours)}
                      unit="h"
                      note={totalHoresMO > 0 ? `${fmt2(g.hours / totalHoresMO * 100)}%` : '0,00%'}
                    />
                  ))}
                  {laborGroups.length === 0 && (
                    <p className="text-muted-foreground text-xs py-1">Sense línies de mà d'obra al descompost</p>
                  )}
                  <div className="border-t my-2" />
                  <ValorsRow label="Total Hores" value={fmt2(totalHoresMO)} unit="h" note={formData.amidament > 0 ? `${fmt2(totalHoresMO / formData.amidament)} h/${formData.unit}` : '—'} bold />
                  <ValorsRow label="Total pes" value={fmt2(formData.cachedTotalWeightKg)} unit="Kg" note={formData.amidament > 0 ? `${fmt2(formData.cachedTotalWeightKg / formData.amidament)} Kg/${formData.unit}` : '—'} />
                  <ValorsRow label="BI / Total pes" value={fmt2(biPerKg)} unit="€/Kg" />
                </div>

                {/* Dades Econòmiques */}
                <div className="lg:border-r lg:pr-10">
                  <div className="flex items-baseline justify-between pb-2 mb-2 border-b gap-3">
                    <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Dades Econòmiques</h5>
                    <div className="flex items-baseline gap-3 text-xs text-muted-foreground">
                      <span className="w-20 shrink-0 text-right">Import</span>
                      <span className="w-24 shrink-0 text-right">Valors/{formData.unit}</span>
                      <span className="w-12 shrink-0 text-right">%</span>
                    </div>
                  </div>
                  <DEconRow label="Base Imposable" value={baseImposable} pct={baseImposable !== 0 ? baseImposable / baseImposable : 0} ua={formData.amidament > 0 ? baseImposable / formData.amidament : null} uaUnit={formData.unit} bold />
                  <DEconRow label="Cost Compres" value={costCompres} pct={baseImposable !== 0 ? costCompres / baseImposable : 0} ua={formData.amidament > 0 ? costCompres / formData.amidament : null} uaUnit={formData.unit} />
                  <DEconRow label="Cost Materials" value={costMaterials} pct={baseImposable !== 0 ? costMaterials / baseImposable : 0} ua={formData.amidament > 0 ? costMaterials / formData.amidament : null} uaUnit={formData.unit} indent />
                  <DEconRow label="Cost Serveis" value={costServeis} pct={baseImposable !== 0 ? costServeis / baseImposable : 0} ua={formData.amidament > 0 ? costServeis / formData.amidament : null} uaUnit={formData.unit} indent />
                  <DEconRow
                    label={(
                      <>
                        <span className="shrink-0">Cost Indirecte</span>
                        <input
                          type="number" step="0.01" min="0" max="100"
                          value={formData.indirectCostPercentage}
                          onChange={(e) => setFormData({ ...formData, indirectCostPercentage: parseFloat(e.target.value) || 0 })}
                          onClick={(e) => e.stopPropagation()}
                          className="w-12 shrink-0 border rounded px-1 py-0.5 text-xs text-right"
                        />
                        <span className="shrink-0">%</span>
                      </>
                    )}
                    value={formData.cachedTotalIndirectCost}
                    pct={baseImposable !== 0 ? formData.cachedTotalIndirectCost / baseImposable : 0}
                    ua={formData.amidament > 0 ? formData.cachedTotalIndirectCost / formData.amidament : null}
                    uaUnit={formData.unit}
                    indent
                  />
                  <DEconRow label="Cost Treball" value={costHores} pct={baseImposable !== 0 ? costHores / baseImposable : 0} ua={formData.amidament > 0 ? costHores / formData.amidament : null} uaUnit={formData.unit} />
                  {laborGroups.map((g) => (
                    <DEconRow key={g.label} label={`Cost Hores ${g.label}`} value={g.cost} pct={baseImposable !== 0 ? g.cost / baseImposable : 0} ua={formData.amidament > 0 ? g.cost / formData.amidament : null} uaUnit={formData.unit} indent />
                  ))}
                  <div className="border-t my-2" />
                  <DEconRow label="Cost Total" value={costTotalDE} pct={baseImposable !== 0 ? costTotalDE / baseImposable : 0} ua={formData.amidament > 0 ? costTotalDE / formData.amidament : null} uaUnit={formData.unit} bold />
                  <DEconRow label="Marge Brut" value={margeBrut} pct={baseImposable !== 0 ? margeBrut / baseImposable : 0} ua={formData.amidament > 0 ? margeBrut / formData.amidament : null} uaUnit={formData.unit} bold accent />
                  <DEconRow label="Valor Afegit" value={valorAfegit} pct={baseImposable !== 0 ? valorAfegit / baseImposable : 0} ua={formData.amidament > 0 ? valorAfegit / formData.amidament : null} uaUnit={formData.unit} bold />
                </div>

                {/* Gràfic Dades Econòmiques */}
                <div className="flex flex-col 2xl:flex-row items-center 2xl:items-center justify-center gap-2 2xl:gap-6">
                  {pieData.length > 0 ? (
                    <>
                      <div className="w-full 2xl:w-[68%] 2xl:min-w-[280px]">
                        <ResponsiveContainer width="100%" height={240} className="2xl:!h-[340px]">
                          <PieChart>
                            <Pie
                              data={pieData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={65}
                              outerRadius={110}
                              paddingAngle={2}
                              animationBegin={0}
                              animationDuration={700}
                            >
                              {pieData.map((d) => <Cell key={d.name} fill={d.color} stroke="none" />)}
                            </Pie>
                            <Tooltip formatter={(v: number) => `${fmt2(v)} €`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="w-full 2xl:w-[32%] mt-1 2xl:mt-0 space-y-1">
                        {pieData.map((d) => (
                          <div key={d.name} className="flex items-center gap-1.5 text-xs">
                            <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                            <span className="flex-1 min-w-0 truncate text-muted-foreground" title={d.name}>{d.name}</span>
                            <span className="tabular-nums shrink-0">{fmt2(d.value)} €</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-muted-foreground text-xs text-center">Sense dades per representar</p>
                  )}
                </div>
            </>
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-4">S'actualitza en desar.</p>
      </div>

      </div>
      </CardContent></Card>
      </>
      )}

      {activeTab === 'descompost' && (
      <>
      {/* ── Lines table ── */}
      <div className="bg-muted/30 border rounded [&_input:not(.hidden)]:bg-white [&_select]:bg-white">
        <div className="w-full bg-slate-100 px-4 py-2 font-medium flex justify-between items-center gap-3 text-[11px] rounded-t">
          <button
            type="button"
            onClick={() => setDetallOpen((v) => !v)}
            className="shrink-0 flex items-center gap-1 hover:opacity-70 transition-opacity"
          >
            {detallOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Detall
          </button>
          <div className="flex items-center gap-2 flex-1 justify-end flex-wrap">
            <Button size="sm" variant="outline" className="text-[11px]" onClick={() => exportDetallToExcel(`${sanitizeFilename(formData.name ? `${formData.name} — Detall` : 'Detall')}.xls`, detallByChapterGroups, undefined, formData.name ? `${formData.name} — Detall` : 'Detall')}>
              <Download className="h-4 w-4 mr-1" /> Excel
            </Button>
            <Button size="sm" variant="outline" className="text-[11px]" onClick={() => printDetallTable(formData.name ? `${formData.name} — Detall` : 'Detall', detallByChapterGroups)}>
              <Printer className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" className="text-[11px]" onClick={() => addLine('MATERIAL')}>
              <Plus className="h-4 w-4 mr-1" /> Afegir línia
            </Button>
          </div>
        </div>

{detallOpen && (
<div className="overflow-x-auto">
          <table ref={tableRef} className="escandall-lines-table" style={{ width: '100%', tableLayout: 'auto', fontSize: '9px', lineHeight: '1.2' }}>
            <colgroup>
              <col style={{ width: '16px' }} />
              <col style={{ width: '22px' }} />
              <col style={{ width: '85px' }} />
              <col style={{ width: '48px' }} />
              <col style={{ width: '230px' }} />
              <col style={{ width: '230px' }} />
              <col style={{ width: '20px' }} />
              <col style={{ width: '230px' }} />
              <col style={{ width: '52px' }} />
              <col style={{ width: '52px' }} />
              <col style={{ width: '52px' }} />
              <col style={{ width: '52px' }} />
              <col style={{ width: '52px' }} />
              <col style={{ width: '62px' }} />
              <col style={{ width: '24px' }} />
              <col style={{ width: '70px' }} />
              <col style={{ width: '70px' }} />
              <col style={{ width: '58px' }} />
              <col style={{ width: '42px' }} />
              <col style={{ width: '45px' }} />
              <col style={{ width: '55px' }} />
              <col style={{ width: '58px' }} />
              <col style={{ width: '48px' }} />
              <col style={{ width: '24px' }} />
            </colgroup>
            <thead className="bg-slate-200">
              <tr>
                <th />
                <th className="px-1 py-0.5 text-left">#</th>
                <th className="px-1 py-0.5 text-left" title="Capítol (editable per línia)">Cap.</th>
                <th className="px-1 py-0.5 text-left">Ref. Plànol</th>
                <th className="px-1 py-0.5 text-left">Descripció</th>
                <th className="px-1 py-0.5 text-left">Operació</th>
                <th className="px-0.5 py-0.5 text-center" title="Imatge tècnica"></th>
                <th className="px-1 py-0.5 text-left">Element</th>
                <th className="px-1 py-0.5 text-center" title="Tipus d'element">Tipus</th>
                <th className="px-1 py-0.5 text-center">UA</th>
                <th className="px-1 py-0.5 text-right">Q.</th>
                <th className="px-1 py-0.5 text-right">Mida A</th>
                <th className="px-1 py-0.5 text-right">Mida B</th>
                <th className="px-1 py-0.5 text-right">Mida C</th>
                <th className="px-1 py-0.5 text-right">Amidament</th>
                <th className="px-1 py-0.5 text-center" title="Preu bloquejat">🔒</th>
                <th className="px-1 py-0.5 text-right">Cost/U</th>
                <th className="px-1 py-0.5 text-right">Cost total</th>
                <th className="px-1 py-0.5 text-right" title="Per defecte, el Marge comercial % del Tipus de Producte de l'element/escandall">Marge</th>
                <th className="px-1 py-0.5 text-right">Descompte</th>
                <th className="px-1 py-0.5 text-right" title="Cost/U × Marge × (1 - Descompte)">Preu</th>
                <th className="px-1 py-0.5 text-right" title="Amidament × Preu">Import</th>
                <th className="px-1 py-0.5 text-right" title="Pes total de la línia (Pes/ut × Amidament)">Pes</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr>
                  <td colSpan={24} className="px-4 py-4 text-center text-muted-foreground text-xs">
                    Cap línia. Afegeix materials o nodes fills.
                  </td>
                </tr>
              )}
              {groupByChapter(lines).sort((a, b) => {
                if (a.chapter === SENSE_CHAPTER) return 1;
                if (b.chapter === SENSE_CHAPTER) return -1;
                return 0;
              }).map((group) => (
                <Fragment key={group.chapter}>
                  {/* Chapter header row — droppable */}
                  <tr
                    className={`border-t border-slate-300 transition-colors ${
                      dropTarget === `chapter:${group.chapter}`
                        ? 'bg-teal-50 outline outline-2 outline-teal-300'
                        : 'bg-slate-50'
                    }`}
                    onDragOver={(e) => { e.preventDefault(); setDropTarget(`chapter:${group.chapter}`); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropTarget(null); }}
                    onDrop={(e) => handleDropOnChapter(e, group.chapter)}
                  >
                    <td colSpan={24}>
                      <div className="flex justify-between items-center px-2 py-0.5">
                        <span className={`font-semibold ${dropTarget === `chapter:${group.chapter}` ? 'text-teal-700' : 'text-slate-600'}`} style={{ fontSize: '9px' }}>
                          {dropTarget === `chapter:${group.chapter}` ? `↓ ${group.chapter}` : group.chapter}
                        </span>
                      </div>
                    </td>
                  </tr>
                  {group.items.map(({ item: line, originalIdx: idx }) => (
                  <tr
                    key={idx}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => { e.preventDefault(); setDropTarget(String(idx)); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropTarget(null); }}
                    onDrop={(e) => handleDropOnLine(e, idx)}
                    className={[
                      'border-b',
                      line.isPriceLocked ? 'bg-amber-50' : 'hover:bg-slate-50',
                      dragFromIdx === idx ? 'opacity-40' : '',
                      dropTarget === String(idx) ? 'border-t-2 border-teal-400' : '',
                    ].filter(Boolean).join(' ')}
                  >
                  {/* Drag handle */}
                  <td
                    data-drag-handle
                    className="px-0.5 py-0 text-center cursor-grab select-none"
                    onPointerDown={() => { pendingDragFromHandleIdx.current = idx; }}
                  >
                    <GripVertical className="h-3.5 w-3.5 text-slate-500 hover:text-slate-800 inline" />
                  </td>
                  {/* # */}
                  <td className="px-1 py-0.5 text-muted-foreground">{idx + 1}</td>

                  {/* Cap. — ghost input amb sugerències dels capítols ja usats */}
                  <td className="px-0.5 py-0.5" style={{ position: 'relative' }}>
                    {(() => {
                      const draft = chapterDrafts[idx] ?? line.chapter ?? '';
                      const usedChapters = [...new Set(
                        lines.map(l => l.chapter?.trim()).filter((c): c is string => !!c)
                      )];
                      const suggestions = usedChapters.filter(c =>
                        c.toLowerCase().includes(draft.toLowerCase()) && c !== draft.trim()
                      );
                      const showSuggestions = chapterSuggestIdx === idx && suggestions.length > 0;
                      return (
                        <>
                          <input
                            type="text"
                            value={draft}
                            onChange={(e) => setChapterDrafts((p) => ({ ...p, [idx]: e.target.value }))}
                            onBlur={(e) => {
                              setTimeout(() => {
                                const val = e.target.value.trim();
                                updateLine(idx, 'chapter', val);
                                setChapterDrafts((p) => { const n = { ...p }; delete n[idx]; return n; });
                                setChapterSuggestIdx(null);
                                (e.target as HTMLInputElement).style.borderColor = 'transparent';
                                (e.target as HTMLInputElement).style.background = 'transparent';
                              }, 150);
                            }}
                            onFocus={(e) => {
                              setChapterDrafts((p) => ({ ...p, [idx]: line.chapter ?? '' }));
                              setChapterSuggestIdx(idx);
                              (e.target as HTMLInputElement).style.borderColor = '#94a3b8';
                              (e.target as HTMLInputElement).style.background = 'white';
                            }}
                            onMouseEnter={(e) => { if (document.activeElement !== e.target) (e.target as HTMLInputElement).style.borderColor = '#cbd5e1'; }}
                            onMouseLeave={(e) => { if (document.activeElement !== e.target) (e.target as HTMLInputElement).style.borderColor = 'transparent'; }}
                            className="w-full h-5 rounded bg-transparent"
                            style={{ fontSize: '9px', padding: '0 2px', border: '1px solid transparent', outline: 'none', cursor: 'pointer' }}
                            title={`Capítol: ${line.chapter?.trim() || 'Sense capítol'} — clica per canviar`}
                            placeholder="·"
                          />
                          {showSuggestions && (
                            <div
                              style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000, minWidth: '120px' }}
                              className="bg-white border border-slate-200 rounded shadow-lg"
                            >
                              {suggestions.map((s) => (
                                <div
                                  key={s}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    updateLine(idx, 'chapter', s);
                                    setChapterDrafts((p) => { const n = { ...p }; delete n[idx]; return n; });
                                    setChapterSuggestIdx(null);
                                  }}
                                  className="px-2 py-1 text-xs cursor-pointer hover:bg-slate-100 whitespace-nowrap"
                                >
                                  {s}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </td>

                  {/* Ref. Planol — text lliure, buit per defecte */}
                  <td className="px-0.5 py-0.5">
                    <Input value={line.referencia ?? ''} onChange={(e) => updateLine(idx, 'referencia', e.target.value)}
                      className="w-full h-5" style={{ fontSize: '9px', padding: '0 4px' }} />
                  </td>

                  {/* Detall — nota lliure per línia (ex. "Soldar", "Muntar conjunt") */}
                  <td className="px-0.5 py-0.5">
                    <Input
                      value={line.detail ?? ''}
                      onChange={(e) => updateLine(idx, 'detail', e.target.value)}
                      className="w-full h-5"
                      style={{ fontSize: '9px', padding: '0 4px' }}
                      placeholder="Nota…"
                    />
                  </td>

                  {/* Operació — text lliure per línia */}
                  <td className="px-0.5 py-0.5">
                    <Input
                      value={line.observations ?? ''}
                      onChange={(e) => updateLine(idx, 'observations', e.target.value)}
                      className="w-full h-5"
                      style={{ fontSize: '9px', padding: '0 4px' }}
                      placeholder="Operació…"
                    />
                  </td>

                  {/* Imatge tècnica de l'element vinculat (material o escandall fill) — miniatura clicable */}
                  <td className="px-0 py-0 text-center">
                    {line.elementImageUrl && (
                      <img
                        src={line.elementImageUrl}
                        alt=""
                        onClick={() => setZoomInstructionUrl(line.elementImageUrl!)}
                        style={{ width: '18px', height: '18px', objectFit: 'cover', borderRadius: '2px', display: 'inline-block', cursor: 'zoom-in' }}
                      />
                    )}
                  </td>

                  {/* Element (Descripció) + autocomplete */}
                  <td className="px-0.5 py-0.5">
                    <Input
                      ref={(el) => { if (el) inputRefs.current.set(idx, el); else inputRefs.current.delete(idx); }}
                      value={line.description}
                      onChange={(e) => {
                        updateLine(idx, 'description', e.target.value);
                        const rect = inputRefs.current.get(idx)?.getBoundingClientRect() ?? null;
                        setDropdownRect(rect);
                        searchItems(e.target.value, idx);
                      }}
                      onBlur={() => setTimeout(clearAutocomplete, 200)}
                      className="w-full h-5"
                      style={{ fontSize: '9px', padding: '0 4px' }}
                      placeholder="Cercar material o escandall..."
                    />
                  </td>

                  {/* Tipus d'element — badge (node) o selector de naturalesa (material) */}
                  <td className="px-0.5 py-0.5 text-center">
                    {(() => { const b = badgeFor(line.childType, line.nature); return line.childType === 'NODE' ? (
                      <span className={`inline-block text-[9px] font-bold px-0.5 rounded leading-3 ${b.cls}`}>{b.text}</span>
                    ) : (
                      <select
                        value={line.nature}
                        onChange={(e) => updateLine(idx, 'nature', e.target.value)}
                        title="Naturalesa de la línia"
                        className={`text-[9px] font-bold px-0.5 rounded leading-3 cursor-pointer border-0 outline-none appearance-none ${b.cls}`}
                        style={{ fontSize: '9px', padding: '0 2px', height: '13px' }}
                      >
                        <option value="MATERIAL">MT</option>
                        <option value="MO_PROPIA" title="Mà d'obra pròpia" style={{ color: '#6d28d9', backgroundColor: '#ede9fe' }}>MO</option>
                        <option value="MO_SUBCONTRACT" title="Mà d'obra subcontractada" style={{ color: '#be123c', backgroundColor: '#ffe4e6' }}>MO</option>
                        <option value="SERVEIS" title="Serveis" style={{ color: '#b45309', backgroundColor: '#fef3c7' }}>SV</option>
                        <option value="ALTRES" title="Altres" style={{ color: '#c2410c', backgroundColor: '#ffedd5' }}>AL</option>
                      </select>
                    ); })()}
                  </td>

                  {/* Unitat */}
                  <td className="px-0.5 py-0.5">
                    <select value={line.unit} onChange={(e) => updateLine(idx, 'unit', e.target.value)}
                      className="w-full border rounded h-5" style={{ fontSize: '9px', padding: '0 2px' }}>
                      {unitTypes.length > 0
                        ? unitTypes.map((ut) => <option key={ut.code} value={ut.code}>{ut.code}</option>)
                        : <option value="Ut">Ut</option>}
                    </select>
                  </td>

                  {/* Quantitat */}
                  <td className="px-0.5 py-0.5">
                    <Input type="text" inputMode="decimal"
                      value={cellFocus[`${idx}-qty`]?.editing ? cellFocus[`${idx}-qty`].draft : (line.quantity === 0 ? '' : fmt2(line.quantity))}
                      onFocus={() => setCellFocus((p) => ({ ...p, [`${idx}-qty`]: { editing: true, draft: line.quantity === 0 ? '' : String(line.quantity) } }))}
                      onChange={(e) => setCellFocus((p) => ({ ...p, [`${idx}-qty`]: { editing: true, draft: e.target.value } }))}
                      onBlur={(e) => { updateLine(idx, 'quantity', parseFloat(e.target.value.replace(',', '.')) || 0); setCellFocus((p) => ({ ...p, [`${idx}-qty`]: { editing: false, draft: '' } })); }}
                      onKeyDown={handleEnterKey}
                      placeholder="0"
                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }} />
                  </td>

                  {/* A */}
                  <td className="px-0.5 py-0.5">
                    <Input type="text" inputMode="decimal"
                      value={cellFocus[`${idx}-a`]?.editing ? cellFocus[`${idx}-a`].draft : (line.dimensionA === 0 ? '' : fmt3(line.dimensionA))}
                      onFocus={() => setCellFocus((p) => ({ ...p, [`${idx}-a`]: { editing: true, draft: line.dimensionA === 0 ? '' : String(line.dimensionA) } }))}
                      onChange={(e) => setCellFocus((p) => ({ ...p, [`${idx}-a`]: { editing: true, draft: e.target.value } }))}
                      onBlur={(e) => { updateLine(idx, 'dimensionA', parseFloat(e.target.value.replace(',', '.')) || 0); setCellFocus((p) => ({ ...p, [`${idx}-a`]: { editing: false, draft: '' } })); }}
                      onKeyDown={handleEnterKey}
                      placeholder="0"
                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }} />
                  </td>

                  {/* B */}
                  <td className="px-0.5 py-0.5">
                    <Input type="text" inputMode="decimal"
                      value={cellFocus[`${idx}-b`]?.editing ? cellFocus[`${idx}-b`].draft : (line.dimensionB === 0 ? '' : fmt3(line.dimensionB))}
                      onFocus={() => setCellFocus((p) => ({ ...p, [`${idx}-b`]: { editing: true, draft: line.dimensionB === 0 ? '' : String(line.dimensionB) } }))}
                      onChange={(e) => setCellFocus((p) => ({ ...p, [`${idx}-b`]: { editing: true, draft: e.target.value } }))}
                      onBlur={(e) => { updateLine(idx, 'dimensionB', parseFloat(e.target.value.replace(',', '.')) || 0); setCellFocus((p) => ({ ...p, [`${idx}-b`]: { editing: false, draft: '' } })); }}
                      onKeyDown={handleEnterKey}
                      placeholder="0"
                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }} />
                  </td>

                  {/* H */}
                  <td className="px-0.5 py-0.5">
                    <Input type="text" inputMode="decimal"
                      value={cellFocus[`${idx}-h`]?.editing ? cellFocus[`${idx}-h`].draft : (line.dimensionH === 0 ? '' : fmt3(line.dimensionH))}
                      onFocus={() => setCellFocus((p) => ({ ...p, [`${idx}-h`]: { editing: true, draft: line.dimensionH === 0 ? '' : String(line.dimensionH) } }))}
                      onChange={(e) => setCellFocus((p) => ({ ...p, [`${idx}-h`]: { editing: true, draft: e.target.value } }))}
                      onBlur={(e) => { updateLine(idx, 'dimensionH', parseFloat(e.target.value.replace(',', '.')) || 0); setCellFocus((p) => ({ ...p, [`${idx}-h`]: { editing: false, draft: '' } })); }}
                      onKeyDown={handleEnterKey}
                      placeholder="0"
                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }} />
                  </td>

                  {/* Mesura (read-only, provisional) */}
                  <td className="px-1 py-0.5 text-right bg-slate-50 whitespace-nowrap">{fmt2(line.measurement)}</td>

                  {/* 🔒 Price lock — disponible per a materials i escandalls (compost) fills */}
                  <td className="px-0.5 py-0.5 text-center">
                    <input
                      type="checkbox"
                      checked={line.isPriceLocked === 1}
                      onChange={(e) => updateLine(idx, 'isPriceLocked', e.target.checked ? 1 : 0)}
                      title="Bloquejar preu — marca-ho i indica un cost manual (Cost/U), ja sigui per un material puntual sense fitxa al catàleg o per sobreescriure el cost directe d'un escandall fill"
                      style={{ width: '12px', height: '12px' }}
                    />
                  </td>

                  {/* Preu Ut:
                      · locked (material o node) → override editable
                      · NODE + unlocked → read-only (cost directe del fill)
                      · MATERIAL + unlocked → read-only (live del backend) */}
                  <td className="px-0.5 py-0.5">
                    {line.isPriceLocked ? (
                      <Input type="text" inputMode="decimal"
                        value={cellFocus[`${idx}-uco`]?.editing ? cellFocus[`${idx}-uco`].draft : ((line.unitCostOverride ?? 0) === 0 ? '' : fmt2(line.unitCostOverride ?? 0))}
                        onFocus={() => setCellFocus((p) => ({ ...p, [`${idx}-uco`]: { editing: true, draft: (line.unitCostOverride ?? 0) === 0 ? '' : String(line.unitCostOverride ?? 0) } }))}
                        onChange={(e) => setCellFocus((p) => ({ ...p, [`${idx}-uco`]: { editing: true, draft: e.target.value } }))}
                        onBlur={(e) => { updateLine(idx, 'unitCostOverride', parseFloat(e.target.value.replace(',', '.')) || 0); setCellFocus((p) => ({ ...p, [`${idx}-uco`]: { editing: false, draft: '' } })); }}
                        onKeyDown={handleEnterKey}
                        placeholder="0"
                        className="w-full h-5 text-right border-amber-400" style={{ fontSize: '9px', padding: '0 4px' }} />
                    ) : line.childType === 'NODE' ? (
                      <div className="px-1 py-0.5 text-right text-teal-700 bg-teal-50 whitespace-nowrap" title="Cost directe del node fill">{fmt2(line.resolvedUnitCost)}</div>
                    ) : (
                      <div className="px-1 py-0.5 text-right text-muted-foreground bg-slate-50 whitespace-nowrap">{fmt2(line.resolvedUnitCost)}</div>
                    )}
                  </td>

                  {/* Cost total (read-only) */}
                  <td className="px-1 py-0.5 text-right font-medium bg-slate-50 whitespace-nowrap">{fmt2(line.lineTotal)}</td>

                  {/* Marge — editable; si no s'ha fixat a la línia, mostra i usa el Marge comercial % del Tipus de Producte */}
                  <td className="px-0.5 py-0.5">
                    <Input type="text" inputMode="decimal"
                      value={cellFocus[`${idx}-marge`]?.editing ? cellFocus[`${idx}-marge`].draft : (line.marge != null ? fmt2(line.marge) : fmt2(marginForLine(line)))}
                      onFocus={() => setCellFocus((p) => ({ ...p, [`${idx}-marge`]: { editing: true, draft: line.marge != null ? String(line.marge) : String(marginForLine(line)) } }))}
                      onChange={(e) => setCellFocus((p) => ({ ...p, [`${idx}-marge`]: { editing: true, draft: e.target.value } }))}
                      onBlur={(e) => {
                        const v = e.target.value.replace(',', '.').trim();
                        updateLine(idx, 'marge', v === '' ? null : (parseFloat(v) || 0));
                        setCellFocus((p) => ({ ...p, [`${idx}-marge`]: { editing: false, draft: '' } }));
                      }}
                      onKeyDown={handleEnterKey}
                      placeholder="0"
                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 4px' }} />
                  </td>

                  {/* Descompte (%) — editable */}
                  <td className="px-0.5 py-0.5">
                    <Input type="text" inputMode="decimal"
                      value={cellFocus[`${idx}-descompte`]?.editing ? cellFocus[`${idx}-descompte`].draft : (line.descompte == null ? '' : fmt2(line.descompte))}
                      onFocus={() => setCellFocus((p) => ({ ...p, [`${idx}-descompte`]: { editing: true, draft: line.descompte == null ? '' : String(line.descompte) } }))}
                      onChange={(e) => setCellFocus((p) => ({ ...p, [`${idx}-descompte`]: { editing: true, draft: e.target.value } }))}
                      onBlur={(e) => {
                        const v = e.target.value.replace(',', '.').trim();
                        updateLine(idx, 'descompte', v === '' ? null : (parseFloat(v) || 0));
                        setCellFocus((p) => ({ ...p, [`${idx}-descompte`]: { editing: false, draft: '' } }));
                      }}
                      onKeyDown={handleEnterKey}
                      placeholder="0"
                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 4px' }} />
                  </td>

                  {/* Preu = Cost/U × Marge × (1 - Descompte%) — read-only */}
                  <td className="px-1 py-0.5 text-right text-muted-foreground bg-slate-50 whitespace-nowrap">
                    {fmt2(Number(line.resolvedUnitCost || 0) * Number(line.marge ?? marginForLine(line)) * (1 - Number(line.descompte ?? 0) / 100))}
                  </td>

                  {/* Import = Amidament × Preu — read-only */}
                  <td className="px-1 py-0.5 text-right font-medium bg-slate-50 whitespace-nowrap">
                    {fmt2(Number(line.measurement || 0) * Number(line.resolvedUnitCost || 0) * Number(line.marge ?? marginForLine(line)) * (1 - Number(line.descompte ?? 0) / 100))}
                  </td>

                  {/* Pes — total de línia (Pes/ut × Amidament); editable: es converteix a Pes/ut internament */}
                  <td className="px-0.5 py-0.5">
                    <Input type="text" inputMode="decimal"
                      value={cellFocus[`${idx}-pes`]?.editing
                        ? cellFocus[`${idx}-pes`].draft
                        : ((line.weightKg * line.measurement) === 0 ? '' : fmt2(line.weightKg * line.measurement))}
                      onFocus={() => setCellFocus((p) => ({ ...p, [`${idx}-pes`]: { editing: true, draft: (line.weightKg * line.measurement) === 0 ? '' : String(Math.round(line.weightKg * line.measurement * 100) / 100) } }))}
                      onChange={(e) => setCellFocus((p) => ({ ...p, [`${idx}-pes`]: { editing: true, draft: e.target.value } }))}
                      onBlur={(e) => {
                        const total = parseFloat(e.target.value.replace(',', '.')) || 0;
                        updateLine(idx, 'weightKg', line.measurement > 0 ? total / line.measurement : total);
                        setCellFocus((p) => ({ ...p, [`${idx}-pes`]: { editing: false, draft: '' } }));
                      }}
                      onKeyDown={handleEnterKey}
                      placeholder="0"
                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 4px' }} />
                  </td>

                  {/* Delete */}
                  <td className="px-0.5 py-0.5 text-center">
                    <button onClick={() => removeLine(idx)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
                  ))}
                  {/* Subtotal del capítol */}
                  <tr className="bg-slate-50 border-b border-slate-300" style={{ fontSize: '9px', height: '24px' }}>
                    <td colSpan={17} className="px-1 py-0.5 text-right text-muted-foreground">Subtotal capítol:</td>
                    <td className="px-1 py-0.5 text-right whitespace-nowrap">
                      {fmt2(group.items.reduce((s, { item }) => s + (item.lineTotal || 0), 0))}
                    </td>
                    <td className="px-1 py-0.5 text-right whitespace-nowrap">
                      {(() => {
                        const informed = group.items.map(({ item }) => item.marge ?? marginForLine(item)).filter((m) => m > 0);
                        return informed.length > 0 ? fmt2(informed.reduce((s, m) => s + m, 0) / informed.length) : '';
                      })()}
                    </td>
                    <td />
                    <td />
                    <td className="px-1 py-0.5 text-right whitespace-nowrap">
                      {fmt2(group.items.reduce((s, { item }) =>
                        s + Number(item.measurement || 0) * Number(item.resolvedUnitCost || 0) * Number(item.marge ?? marginForLine(item)) * (1 - Number(item.descompte ?? 0) / 100), 0))}
                    </td>
                    <td className="px-1 py-0.5 text-right whitespace-nowrap">
                      {fmt2(group.items.reduce((s, { item }) => s + (item.weightKg * item.measurement || 0), 0))} Kg
                    </td>
                    <td />
                  </tr>
                </Fragment>
              ))}
            </tbody>
            {lines.length > 0 && (
              <tfoot className="bg-slate-100 font-medium" style={{ fontSize: '9px' }}>
                <tr style={{ height: '24px' }}>
                  <td colSpan={17} className="px-1 py-0.5 text-right">Totals:</td>
                  <td className="px-1 py-0.5 text-right whitespace-nowrap">
                    {fmt2(lines.reduce((s, l) => s + (l.lineTotal || 0), 0))}
                  </td>
                  <td className="px-1 py-0.5 text-right whitespace-nowrap">
                    {(() => {
                      const informed = lines.map((l) => l.marge ?? marginForLine(l)).filter((m) => m > 0);
                      return informed.length > 0 ? fmt2(informed.reduce((s, m) => s + m, 0) / informed.length) : '';
                    })()}
                  </td>
                  <td />
                  <td />
                  <td className="px-1 py-0.5 text-right whitespace-nowrap">
                    {fmt2(totalImport)}
                  </td>
                  <td className="px-1 py-0.5 text-right whitespace-nowrap" title="Σ Pes de línia (Pes/ut × Amidament)">
                    {fmt2(lines.reduce((s, l) => s + (l.weightKg * l.measurement || 0), 0))} Kg
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        )}
      </div>

      {/* ── Detall d'elements / Detall descompost ── */}
      <DetallCollapsiblePanel title="Detall d'elements" nodeName={formData.name} groups={detallElementsGroups} />
      <DetallCollapsiblePanel title="Detall descompost" nodeName={formData.name} groups={detallDescompostGroups} withPctImport />

      {/* ── Arbre de costos (Fase 3c) ── */}
      {!isNew && (
        <NodeCostTree lines={lines} rootNodeId={id!} rootName={formData.name} />
      )}
      </>
      )}

      {activeTab === 'instruccions' && (
      <div className="bg-card border rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-foreground">Instruccions de muntatge / configuració</h2>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="text-[11px]" onClick={() => exportInstruccionsToExcel(`${sanitizeFilename(formData.name ? `${formData.name} — Instruccions` : 'Instruccions')}.xls`, instructions, formData.name ? `${formData.name} — Instruccions` : 'Instruccions')}>
              <Download className="h-4 w-4 mr-1" /> Excel
            </Button>
            <Button size="sm" variant="outline" className="text-[11px]" onClick={() => printInstruccions(formData.name ? `${formData.name} — Instruccions` : 'Instruccions', instructions)}>
              <Printer className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" className="text-[11px]" onClick={addInstruction}>
              <Plus className="h-4 w-4 mr-1" /> Afegir instrucció
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ fontSize: '11px', lineHeight: '1.3' }}>
            <thead className="bg-slate-200">
              <tr>
                <th className="px-1 py-1 text-left font-medium w-16">Nº</th>
                <th className="px-2 py-1 text-left font-medium w-48">Referència</th>
                <th className="px-2 py-1 text-left font-medium">Descripció</th>
                <th className="px-1 py-1 text-center font-medium w-16">Foto</th>
                <th className="px-1 py-1 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {instructions.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-2 py-3 text-center text-muted-foreground">
                    Cap instrucció — prem "Afegir instrucció"
                  </td>
                </tr>
              )}
              {instructions.map((row, idx) => (
                <tr
                  key={idx}
                  draggable
                  onDragStart={() => handleInstructionDragStart(idx)}
                  onDragEnd={handleInstructionDragEnd}
                  onDragOver={(e) => { e.preventDefault(); setDropInstructionTarget(idx); }}
                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as globalThis.Node)) setDropInstructionTarget(null); }}
                  onDrop={() => handleInstructionDrop(idx)}
                  className={[
                    idx % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                    dragInstructionIdx === idx ? 'opacity-40' : '',
                    dropInstructionTarget === idx ? 'border-t-2 border-amber-400' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <td className="px-1 py-1 text-muted-foreground">
                    <span className="inline-flex items-center gap-1 cursor-grab" title="Arrossega per reordenar">
                      <GripVertical size={14} className="text-gray-500 hover:text-gray-800" />
                      {idx + 1}
                    </span>
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      value={row.chapter}
                      onChange={(e) => updateInstruction(idx, 'chapter', e.target.value)}
                      className="w-full h-6"
                      style={{ fontSize: '11px' }}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      value={row.comment}
                      onChange={(e) => updateInstruction(idx, 'comment', e.target.value)}
                      className="w-full h-6"
                      style={{ fontSize: '11px' }}
                    />
                  </td>
                  <td className="px-1 py-1 text-center">
                    {row.photoUrl ? (
                      <div className="relative inline-block w-10 h-10 group">
                        <img
                          src={row.photoUrl}
                          alt="Foto"
                          className="w-10 h-10 object-cover rounded border border-gray-200 cursor-zoom-in"
                          onClick={() => setZoomInstructionUrl(row.photoUrl!)}
                        />
                        <button
                          type="button"
                          onClick={() => clearInstructionPhoto(idx)}
                          className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-gray-200 p-0.5 opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-600"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ) : (
                      <label className="inline-flex items-center justify-center w-10 h-10 border border-dashed border-gray-300 rounded cursor-pointer hover:bg-gray-50 text-muted-foreground">
                        {uploadingInstructionIdx === idx ? (
                          <RefreshCw size={14} className="animate-spin" />
                        ) : (
                          <Upload size={14} />
                        )}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleInstructionPhotoUpload(idx, f); e.target.value = ''; }}
                        />
                      </label>
                    )}
                  </td>
                  <td className="px-1 py-1 text-center">
                    <button type="button" onClick={() => removeInstruction(idx)} className="text-muted-foreground hover:text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {activeTab === 'documents' && (
      <div className="bg-card border rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <Paperclip className="h-4 w-4 text-rose-500" />
          <h2 className="text-sm font-semibold text-foreground">Documentació</h2>
          {documents.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">({documents.length})</span>
          )}
        </div>
        {isNew ? (
          <p className="text-muted-foreground text-xs text-center py-4">Desa l'escandall abans de pujar documents.</p>
        ) : (
          <>
            <label
              onDragOver={(e) => { e.preventDefault(); setDocsDragOver(true); }}
              onDragLeave={() => setDocsDragOver(false)}
              onDrop={handleDocumentsDrop}
              className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-lg py-4 cursor-pointer transition-colors ${
                docsDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Upload size={18} className="text-gray-400" />
              <span className="text-xs text-muted-foreground">
                {uploadingDoc ? 'Pujant...' : 'Arrossega o clica per pujar un document'}
              </span>
              <input
                type="file"
                className="hidden"
                disabled={uploadingDoc}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUploadDocument(file);
                  e.target.value = '';
                }}
              />
            </label>

            {documents.length === 0 ? (
              <p className="text-muted-foreground text-xs text-center py-3">Encara no hi ha cap document</p>
            ) : (
              <div className="mt-3 space-y-1">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50 text-xs">
                    <FileText size={14} className="text-gray-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium" title={doc.name}>{doc.name}</div>
                      <div className="text-muted-foreground">{fmtFileSize(doc.fileSize)} · {new Date(doc.createdAt).toLocaleDateString('ca-ES')}</div>
                    </div>
                    <button type="button" title="Visualitzar" onClick={() => handleOpenDocument(doc, false)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors">
                      <Eye size={14} />
                    </button>
                    <button type="button" title="Descarregar" onClick={() => handleOpenDocument(doc, true)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-green-600 transition-colors">
                      <Download size={14} />
                    </button>
                    <button type="button" title="Eliminar" onClick={() => handleDeleteDocument(doc.id)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* ── Lightbox portal ── */}
      {lightboxOpen && formData.imageUrl && createPortal(
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10000] cursor-zoom-out"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white hover:text-slate-300"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={formData.imageUrl}
            alt="Imatge escandall"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body,
      )}

      {/* ── Lightbox foto d'instrucció ── */}
      {zoomInstructionUrl && createPortal(
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10000] cursor-zoom-out"
          onClick={() => setZoomInstructionUrl(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white hover:text-slate-300"
            onClick={() => setZoomInstructionUrl(null)}
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={zoomInstructionUrl}
            alt="Foto instrucció"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body,
      )}

      {/* ── Modal Importar com a plantilla ── */}
      {templatePopupOpen && createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]" onClick={() => setTemplatePopupOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-[480px] max-h-[60vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <h2 className="font-semibold text-foreground text-sm">Importar com a plantilla</h2>
              <input autoFocus className="flex-1 px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Cercar per nom o codi..."
                value={templateSearch} onChange={(e) => setTemplateSearch(e.target.value)} />
            </div>
            <div className="overflow-y-auto flex-1">
              {templateNodes
                .filter((n) => !templateSearch
                  || n.name.toLowerCase().includes(templateSearch.toLowerCase())
                  || (n.code ?? '').toLowerCase().includes(templateSearch.toLowerCase()))
                .slice(0, 50)
                .map((n) => (
                  <button key={n.id} disabled={importingTemplate} onClick={() => importTemplate(n)}
                    className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 text-xs disabled:opacity-50">
                    <span className="font-medium text-foreground">{n.name}</span>
                    {n.code && <span className="ml-2 text-muted-foreground text-xs">{n.code}</span>}
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Cost directe {n.cachedTotalDirectCost?.toFixed?.(2) ?? '0.00'} €
                    </div>
                  </button>
                ))}
              {templateNodes.length === 0 && (
                <p className="p-4 text-xs text-muted-foreground text-center">No s'han trobat escandalls</p>
              )}
            </div>
            <div className="p-3 border-t border-gray-100 flex justify-end">
              <button onClick={() => setTemplatePopupOpen(false)} className="px-3 py-1.5 text-xs text-muted-foreground hover:bg-gray-100 rounded">
                Cancel·lar
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Autocomplete dropdown portal ── */}
      {activeSearch !== null && dropdownRect && createPortal(
        <div
          style={{
            position: 'fixed',
            ...(dropdownRect.bottom + 220 > window.innerHeight
              ? { bottom: window.innerHeight - dropdownRect.top + 2 }
              : { top: dropdownRect.bottom + 2 }),
            left: dropdownRect.left,
            minWidth: dropdownRect.width,
            width: 'max-content',
            maxWidth: 440,
            zIndex: 9999,
          }}
          className="bg-white border rounded-md shadow-xl max-h-52 overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {searching && <div className="px-3 py-2 text-muted-foreground text-xs">Cercant...</div>}
          {!searching && materialSuggestions.length === 0 && nodeSuggestions.length === 0 && (
            <div className="px-3 py-2 text-muted-foreground text-xs">Sense resultats</div>
          )}
          {!searching && materialSuggestions.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-slate-50">Materials</div>
              {materialSuggestions.map((mat) => (
                <div key={mat.id} className="px-3 py-2 hover:bg-slate-100 cursor-pointer border-b last:border-b-0"
                  onMouseDown={() => selectMaterial(mat, activeSearch.lineIndex)}>
                  <div className="font-medium text-xs whitespace-normal">{mat.name}</div>
                  <div className="text-muted-foreground text-xs whitespace-normal">
                    {mat.code} | {mat.unit} | {formatDecimal(Number(mat.pricePerMagnitude ?? mat.costPerUnit ?? 0))} €/ut
                  </div>
                </div>
              ))}
            </>
          )}
          {!searching && nodeSuggestions.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-slate-50">Escandalls</div>
              {nodeSuggestions.map((node) => (
                <div key={node.id} className="px-3 py-2 hover:bg-slate-100 cursor-pointer border-b last:border-b-0"
                  onMouseDown={() => selectNode(node, activeSearch.lineIndex)}>
                  <div className="font-medium text-xs whitespace-normal">{node.name}</div>
                  <div className="text-muted-foreground text-xs">
                    {node.code ? `${node.code} | ` : ''}{node.unit} | Cost directe: {formatDecimal(Number(node.cachedTotalDirectCost || 0))} €
                  </div>
                </div>
              ))}
            </>
          )}
        </div>,
        document.body,
      )}

    </div>
  );
}
